//src/components/maps/EditableShipmentRouteMap.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef, 
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import {
  LoaderCircle,
  MapPin,
  Search,
  X,
} from "lucide-react";

import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/EditableShipmentRouteMap.css";

const MAP_STYLE =
  "/maps/styles/loadevo-road.json?v=5";

const ROUTE_CACHE_STORAGE_KEY =
  "loadevo:dispatcher:editable-route-cache:v2";

const ROUTE_CACHE_FRESH_MS =
  10 *
  60 *
  1000;

const ROUTE_CACHE_MAX_AGE_MS =
  24 *
  60 *
  60 *
  1000;

const ROUTE_CACHE_MAX_ENTRIES =
  40;

const routeMemoryCache =
  new Map();

function readStoredRouteCache() {
  if (
    typeof window ===
    "undefined"
  ) {
    return {};
  }

  try {
    const storedValue =
      window.localStorage.getItem(
        ROUTE_CACHE_STORAGE_KEY
      );

    if (
      !storedValue
    ) {
      return {};
    }

    const parsedValue =
      JSON.parse(
        storedValue
      );

    if (
      !parsedValue ||
      typeof parsedValue !==
        "object" ||
      Array.isArray(
        parsedValue
      )
    ) {
      return {};
    }

    return parsedValue;
  } catch (
    error
  ) {
    console.warn(
      "[EditableShipmentRouteMap] Could not read route cache:",
      error
    );

    return {};
  }
}

function writeStoredRouteCache(
  storedCache
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      ROUTE_CACHE_STORAGE_KEY,
      JSON.stringify(
        storedCache
      )
    );
  } catch (
    error
  ) {
    console.warn(
      "[EditableShipmentRouteMap] Could not save route cache:",
      error
    );
  }
}

function getCachedRoute(
  routeCacheKey
) {
  if (
    !routeCacheKey
  ) {
    return null;
  }

  const now =
    Date.now();

  const memoryEntry =
    routeMemoryCache.get(
      routeCacheKey
    );

  if (
    memoryEntry &&
    now -
      Number(
        memoryEntry.createdAt ||
          0
      ) <=
      ROUTE_CACHE_MAX_AGE_MS
  ) {
    return {
      ...memoryEntry,

      isFresh:
        now -
          Number(
            memoryEntry.createdAt ||
              0
          ) <=
        ROUTE_CACHE_FRESH_MS,
    };
  }

  if (
    memoryEntry
  ) {
    routeMemoryCache.delete(
      routeCacheKey
    );
  }

  const storedCache =
    readStoredRouteCache();

  const storedEntry =
    storedCache[
      routeCacheKey
    ];

  if (
    !storedEntry
  ) {
    return null;
  }

  const storedAge =
    now -
    Number(
      storedEntry.createdAt ||
        0
    );

  if (
    storedAge >
    ROUTE_CACHE_MAX_AGE_MS
  ) {
    delete storedCache[
      routeCacheKey
    ];

    writeStoredRouteCache(
      storedCache
    );

    return null;
  }

  routeMemoryCache.set(
    routeCacheKey,
    storedEntry
  );

  return {
    ...storedEntry,

    isFresh:
      storedAge <=
      ROUTE_CACHE_FRESH_MS,
  };
}

function setCachedRoute(
  routeCacheKey,
  routeResult
) {
  if (
    !routeCacheKey ||
    !routeResult?.geometry
  ) {
    return;
  }

  const cacheEntry = {
    routeResult,

    createdAt:
      Date.now(),
  };

  routeMemoryCache.set(
    routeCacheKey,
    cacheEntry
  );

  const storedCache =
    readStoredRouteCache();

  storedCache[
    routeCacheKey
  ] =
    cacheEntry;

  const orderedEntries =
    Object.entries(
      storedCache
    )
      .filter(
        (
          [
            ,
            entry,
          ]
        ) =>
          entry &&
          Date.now() -
            Number(
              entry.createdAt ||
                0
            ) <=
            ROUTE_CACHE_MAX_AGE_MS
      )
      .sort(
        (
          [
            ,
            entryA,
          ],
          [
            ,
            entryB,
          ]
        ) =>
          Number(
            entryB.createdAt ||
              0
          ) -
          Number(
            entryA.createdAt ||
              0
          )
      )
      .slice(
        0,
        ROUTE_CACHE_MAX_ENTRIES
      );

  writeStoredRouteCache(
    Object.fromEntries(
      orderedEntries
    )
  );
}

const DEFAULT_CENTER = [
  -8.61099,
  41.14961,
];

const DEFAULT_ZOOM =
  6;

class MapZoomLevelControl {
  onAdd(
    map
  ) {
    this.map =
      map;

    this.container =
      document.createElement(
        "div"
      );

    this.container.className =
      "maplibregl-ctrl editable-shipment-route-map__zoom-level";

    this.container.setAttribute(
      "role",
      "status"
    );

    this.container.setAttribute(
      "aria-label",
      "Nível atual de zoom do mapa"
    );

    this.labelElement =
      document.createElement(
        "span"
      );

    this.labelElement.textContent =
      "Zoom";

    this.valueElement =
      document.createElement(
        "strong"
      );

    this.tileElement =
      document.createElement(
        "small"
      );

    this.container.append(
      this.labelElement,
      this.valueElement,
      this.tileElement
    );

    this.handleZoom =
      () => {
        const zoom =
          Number(
            this.map?.getZoom()
          );

        if (
          !Number.isFinite(
            zoom
          )
        ) {
          return;
        }

        const tileZoom =
          Math.floor(
            zoom
          );

        this.valueElement.textContent =
          zoom.toFixed(
            2
          );

        this.tileElement.textContent =
          `tile z${tileZoom}`;

        this.container.title =
          [
            `Zoom exato: ${zoom.toFixed(4)}`,
            `Nível dos tiles: z${tileZoom}`,
          ].join(
            "\n"
          );
      };

    this.map.on(
      "zoom",
      this.handleZoom
    );

    this.handleZoom();

    return this.container;
  }

  onRemove() {
    if (
      this.map &&
      this.handleZoom
    ) {
      this.map.off(
        "zoom",
        this.handleZoom
      );
    }

    this.container?.remove();

    this.map =
      null;

    this.container =
      null;

    this.labelElement =
      null;

    this.valueElement =
      null;

    this.tileElement =
      null;

    this.handleZoom =
      null;
  }
}

const ROUTE_SOURCE_ID =
  "shipper-request-edit-route";

const ROUTE_CASING_LAYER_ID =
  "shipper-request-edit-route-casing";

const ROUTE_LINE_LAYER_ID =
  "shipper-request-edit-route-line";

const ROAD_SHIELDS_SOURCE_ID =
  "loadevo-portugal-road-network";

const ROAD_SHIELDS_SOURCE_LAYER =
  "road_shield_anchors";

const ROAD_LINES_SOURCE_LAYER =
  "road_lines";

const ROAD_RESTRICTIONS_SOURCE_LAYER =
  "vehicle_restrictions";

const ROAD_RESTRICTION_CASING_LAYER_ID =
  "loadevo-vehicle-restrictions-casing";

const ROAD_RESTRICTION_HARD_LAYER_ID =
  "loadevo-vehicle-restrictions-hard";

const ROAD_RESTRICTION_CONDITIONAL_LAYER_ID =
  "loadevo-vehicle-restrictions-conditional";

const REJECTED_ROAD_SOURCE_ID =
  "loadevo-rejected-road";

const REJECTED_ROAD_GLOW_LAYER_ID =
  "loadevo-rejected-road-glow";

const REJECTED_ROAD_LINE_LAYER_ID =
  "loadevo-rejected-road-line";

const ROAD_LINE_MOTORWAY_CASING_LAYER_ID =
  "loadevo-road-lines-motorway-casing";

const ROAD_LINE_MOTORWAY_LAYER_ID =
  "loadevo-road-lines-motorway";

const ROAD_LINE_PRINCIPAL_LAYER_ID =
  "loadevo-road-lines-principal";

const ROAD_LINE_NATIONAL_LAYER_ID =
  "loadevo-road-lines-national";

const ROAD_LINE_REGIONAL_LAYER_ID =
  "loadevo-road-lines-regional";

const ROAD_LINE_MUNICIPAL_LAYER_ID =
  "loadevo-road-lines-municipal";

const ROAD_SHIELDS_PM_TILES_PATH =
  "/maps/road-network/portugal-road-network.pmtiles?v=12";

const EUROPE_MOTORWAY_SOURCE_ID =
  "loadevo-europe-motorway-lines";

const EUROPE_MOTORWAY_SOURCE_LAYER =
  "road_lines";

const EUROPE_MOTORWAY_PM_TILES_PATH =
  "/maps/road-network/europe-motorway-lines-v3.pmtiles?v=3";

const ROAD_SHIELD_MOTORWAY_ICON_ID =
  "loadevo-road-shield-motorway";

const ROAD_SHIELD_EUROPEAN_ICON_ID =
  "loadevo-road-shield-european";

const ROAD_SHIELD_PRINCIPAL_ICON_ID =
  "loadevo-road-shield-principal";

const ROAD_SHIELD_NATIONAL_ICON_ID =
  "loadevo-road-shield-national";

const ROAD_SHIELD_REGIONAL_ICON_ID =
  "loadevo-road-shield-regional";

const ROAD_SHIELD_MUNICIPAL_ICON_ID =
  "loadevo-road-shield-municipal";

const ROAD_SHIELD_ALL_LAYER_ID =
  "loadevo-road-shields";

const EUROPE_ROAD_SHIELD_LAYER_ID =
  "loadevo-europe-road-shields";

const EUROPE_MOTORWAY_SHIELD_LAYER_ID =
  "loadevo-europe-motorway-shields";

const EUROPE_MOTORWAY_CASING_LAYER_ID =
  "loadevo-europe-motorways-casing";

const EUROPE_MOTORWAY_LINE_LAYER_ID =
  "loadevo-europe-motorways";

const BASE_ROAD_SHIELD_LAYER_IDS = [
  "highway-shield-non-us",
  "highway-shield-us-interstate",
  "road_shield_us",
];

const PORTUGAL_ROAD_NETWORK_LAYER_IDS = [
  ROAD_SHIELD_ALL_LAYER_ID,
  ROAD_RESTRICTION_CONDITIONAL_LAYER_ID,
  ROAD_RESTRICTION_HARD_LAYER_ID,
  ROAD_RESTRICTION_CASING_LAYER_ID,
  ROAD_LINE_MOTORWAY_LAYER_ID,
  ROAD_LINE_MOTORWAY_CASING_LAYER_ID,
  ROAD_LINE_PRINCIPAL_LAYER_ID,
  ROAD_LINE_NATIONAL_LAYER_ID,
  ROAD_LINE_REGIONAL_LAYER_ID,
  ROAD_LINE_MUNICIPAL_LAYER_ID,
];

const ROAD_SHIELD_IMAGE_IDS =
  new Set([
    ROAD_SHIELD_MOTORWAY_ICON_ID,
    ROAD_SHIELD_EUROPEAN_ICON_ID,
    ROAD_SHIELD_PRINCIPAL_ICON_ID,
    ROAD_SHIELD_NATIONAL_ICON_ID,
    ROAD_SHIELD_REGIONAL_ICON_ID,
    ROAD_SHIELD_MUNICIPAL_ICON_ID,
  ]);

const ROAD_NETWORK_RETRY_DELAYS_MS = [
  350,
  1000,
  2500,
];

const PM_TILES_PROTOCOL_GLOBAL_KEY =
  "__loadevoPmtilesProtocol";

function ensurePmtilesProtocol() {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  if (
    window[
      PM_TILES_PROTOCOL_GLOBAL_KEY
    ]
  ) {
    return;
  }

  const protocol =
    new Protocol();

  maplibregl.addProtocol(
    "pmtiles",
    protocol.tile
  );

  window[
    PM_TILES_PROTOCOL_GLOBAL_KEY
  ] =
    protocol;
}

function drawRoundedRectangle(
  context,
  x,
  y,
  width,
  height,
  radius
) {
  const safeRadius =
    Math.min(
      radius,
      width /
        2,
      height /
        2
    );

  context.beginPath();

  context.moveTo(
    x +
      safeRadius,
    y
  );

  context.lineTo(
    x +
      width -
      safeRadius,
    y
  );

  context.quadraticCurveTo(
    x +
      width,
    y,
    x +
      width,
    y +
      safeRadius
  );

  context.lineTo(
    x +
      width,
    y +
      height -
      safeRadius
  );

  context.quadraticCurveTo(
    x +
      width,
    y +
      height,
    x +
      width -
      safeRadius,
    y +
      height
  );

  context.lineTo(
    x +
      safeRadius,
    y +
      height
  );

  context.quadraticCurveTo(
    x,
    y +
      height,
    x,
    y +
      height -
      safeRadius
  );

  context.lineTo(
    x,
    y +
      safeRadius
  );

  context.quadraticCurveTo(
    x,
    y,
    x +
      safeRadius,
    y
  );

  context.closePath();
}

function createStretchableRoadShieldImage({
  backgroundColor,
  borderColor,
}) {
  const pixelRatio =
    2;

  const logicalWidth =
    34;

  const logicalHeight =
    20;

  const width =
    logicalWidth *
    pixelRatio;

  const height =
    logicalHeight *
    pixelRatio;

  const canvas =
    document.createElement(
      "canvas"
    );

  canvas.width =
    width;

  canvas.height =
    height;

  const context =
    canvas.getContext(
      "2d"
    );

  if (
    !context
  ) {
    return null;
  }

  context.clearRect(
    0,
    0,
    width,
    height
  );

  context.fillStyle =
    backgroundColor;

  context.strokeStyle =
    borderColor;

  context.lineWidth =
    1 *
    pixelRatio;

  drawRoundedRectangle(
    context,
    1 *
      pixelRatio,
    1 *
      pixelRatio,
    width -
      2 *
        pixelRatio,
    height -
      2 *
        pixelRatio,
    3 *
      pixelRatio
  );

  context.fill();

  context.stroke();

  return {
    image:
      context.getImageData(
        0,
        0,
        width,
        height
      ),

    options: {
      pixelRatio,

      content: [
        5 *
          pixelRatio,
        3 *
          pixelRatio,
        width -
          5 *
            pixelRatio,
        height -
          3 *
            pixelRatio,
      ],

      stretchX: [
        [
          9 *
            pixelRatio,
          width -
            9 *
              pixelRatio,
        ],
      ],

      stretchY: [
        [
          6 *
            pixelRatio,
          height -
            6 *
              pixelRatio,
        ],
      ],
    },
  };
}

function ensureRoadShieldImage(
  map,
  imageId,
  configuration
) {
  if (
    map.hasImage(
      imageId
    )
  ) {
    return;
  }

  const shieldImage =
    createStretchableRoadShieldImage(
      configuration
    );

  if (
    !shieldImage
  ) {
    return;
  }

  map.addImage(
    imageId,
    shieldImage.image,
    shieldImage.options
  );
}

function ensureRoadShieldImages(
  map
) {
  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_MOTORWAY_ICON_ID,
    {
      backgroundColor:
        "#1f6fb2",

      borderColor:
        "#ffffff",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_EUROPEAN_ICON_ID,
    {
      backgroundColor:
        "#3f8f3f",

      borderColor:
        "#ffffff",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_PRINCIPAL_ICON_ID,
    {
      backgroundColor:
        "#1e5da8",

      borderColor:
        "#ffffff",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_NATIONAL_ICON_ID,
    {
      backgroundColor:
        "#ffffff",

      borderColor:
        "#475569",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_REGIONAL_ICON_ID,
    {
      backgroundColor:
        "#fff7cc",

      borderColor:
        "#92711b",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_MUNICIPAL_ICON_ID,
    {
      backgroundColor:
        "#f8fafc",

      borderColor:
        "#64748b",
    }
  );
}

function addRoadShieldLayer(
  map
) {
  if (
    map.getLayer(
      ROAD_SHIELD_ALL_LAYER_ID
    )
  ) {
    return;
  }

  map.addLayer({
    id:
      ROAD_SHIELD_ALL_LAYER_ID,

    type:
      "symbol",

    source:
      ROAD_SHIELDS_SOURCE_ID,

    "source-layer":
      ROAD_SHIELDS_SOURCE_LAYER,

    minzoom:
      5,

    maxzoom:
      16,

    filter: [
      "in",

      [
        "get",
        "road_class",
      ],

      [
        "literal",

        [
          "motorway",
          "european",
          "principal",
          "national",
          "regional",
          "municipal",
        ],
      ],
    ],

    layout: {
      "symbol-placement":
        "point",

      "symbol-z-order":
        "source",

      "symbol-sort-key": [
        "coalesce",

        [
          "to-number",

          [
            "get",
            "priority",
          ],
        ],

        99,
      ],

      "symbol-avoid-edges":
        true,

      "text-field": [
        "to-string",

        [
          "get",
          "ref",
        ],
      ],

      "text-font": [
        "Noto Sans Bold",
      ],

      "text-size": [
        "interpolate",

        [
          "linear",
        ],

        [
          "zoom",
        ],

        5,
        8.5,

        7,
        9,

        9,
        10,

        12,
        10.5,

        15,
        11,
      ],

      "text-letter-spacing":
        0,

      "text-padding":
        2,

      "text-rotation-alignment":
        "viewport",

      "text-pitch-alignment":
        "viewport",

      "text-keep-upright":
        true,

      "text-allow-overlap":
        false,

      "text-ignore-placement":
        false,

      "text-optional":
        false,

      "icon-image": [
        "match",

        [
          "get",
          "road_class",
        ],

        "motorway",
        ROAD_SHIELD_MOTORWAY_ICON_ID,

        "european",
        ROAD_SHIELD_EUROPEAN_ICON_ID,

        "principal",
        ROAD_SHIELD_PRINCIPAL_ICON_ID,

        "national",
        ROAD_SHIELD_NATIONAL_ICON_ID,

        "regional",
        ROAD_SHIELD_REGIONAL_ICON_ID,

        "municipal",
        ROAD_SHIELD_MUNICIPAL_ICON_ID,

        ROAD_SHIELD_NATIONAL_ICON_ID,
      ],

      "icon-text-fit":
        "both",

      "icon-text-fit-padding": [
        1.5,
        4,
        1.5,
        4,
      ],

      "icon-padding": [
        "interpolate",

        [
          "linear",
        ],

        [
          "zoom",
        ],

        5,
        24,

        7,
        18,

        9,
        12,

        11,
        8,

        14,
        4,
      ],

      "icon-rotation-alignment":
        "viewport",

      "icon-pitch-alignment":
        "viewport",

      "icon-allow-overlap":
        false,

      "icon-ignore-placement":
        false,

      "icon-optional":
        false,
    },

    paint: {
      "text-color": [
        "match",

        [
          "get",
          "road_class",
        ],

        "motorway",
        "#ffffff",

        "european",
        "#ffffff",

        "principal",
        "#ffffff",

        "national",
        "#334155",

        "regional",
        "#715517",

        "municipal",
        "#475569",

        "#334155",
      ],

      "text-halo-width":
        0,
    },
  });
}

function buildPortugalRoadNetworkSourceUrl(
  reloadKey = ""
) {
  const absoluteArchiveUrl =
    new URL(
      ROAD_SHIELDS_PM_TILES_PATH,
      window.location.origin
    );

  if (
    reloadKey
  ) {
    absoluteArchiveUrl.searchParams.set(
      "reload",
      String(
        reloadKey
      )
    );
  }

  return `pmtiles://${absoluteArchiveUrl.toString()}`;
}

function ensurePortugalRoadNetworkSource(
  map,
  {
    reloadKey = "",
  } = {}
) {
  if (
    map.getSource(
      ROAD_SHIELDS_SOURCE_ID
    )
  ) {
    return;
  }

  map.addSource(
    ROAD_SHIELDS_SOURCE_ID,
    {
      type:
        "vector",

      url:
        buildPortugalRoadNetworkSourceUrl(
          reloadKey
        ),

      attribution:
        "© OpenStreetMap contributors",
    }
  );
}

function buildEuropeMotorwaySourceUrl() {
  const absoluteArchiveUrl =
    new URL(
      EUROPE_MOTORWAY_PM_TILES_PATH,
      window.location.origin
    );

  return `pmtiles://${absoluteArchiveUrl.toString()}`;
}

function ensureEuropeMotorwaySource(
  map
) {
  if (
    map.getSource(
      EUROPE_MOTORWAY_SOURCE_ID
    )
  ) {
    return;
  }

  map.addSource(
    EUROPE_MOTORWAY_SOURCE_ID,
    {
      type:
        "vector",

      url:
        buildEuropeMotorwaySourceUrl(),

      attribution:
        "© OpenStreetMap contributors",
    }
  );
}

function removePortugalRoadNetworkSource(
  map
) {
  PORTUGAL_ROAD_NETWORK_LAYER_IDS.forEach(
    (
      layerId
    ) => {
      if (
        !map.getLayer(
          layerId
        )
      ) {
        return;
      }

      try {
        map.removeLayer(
          layerId
        );
      } catch (
        error
      ) {
        console.warn(
          `[EditableShipmentRouteMap] Could not remove road-network layer "${layerId}":`,
          error
        );
      }
    }
  );

  if (
    !map.getSource(
      ROAD_SHIELDS_SOURCE_ID
    )
  ) {
    return;
  }

  try {
    map.removeSource(
      ROAD_SHIELDS_SOURCE_ID
    );
  } catch (
    error
  ) {
    console.warn(
      "[EditableShipmentRouteMap] Could not remove the Portuguese road-network source:",
      error
    );
  }
}

function getFirstMapSymbolLayerId(
  map
) {
  const styleLayers =
    map.getStyle()
      ?.layers ||
    [];

  return (
    styleLayers.find(
      (
        layer
      ) =>
        layer.type ===
        "symbol"
    )?.id ||
    undefined
  );
}

function addRoadNetworkLineLayer(
  map,
  {
    id,
    roadClasses,
    minzoom,
    lineColor,
    minimumWidth,
    maximumWidth,
    beforeId,
  }
) {
  if (
    map.getLayer(
      id
    )
  ) {
    return;
  }

  map.addLayer(
    {
      id,

      type:
        "line",

      source:
        ROAD_SHIELDS_SOURCE_ID,

      "source-layer":
        ROAD_LINES_SOURCE_LAYER,

      minzoom,

      maxzoom:
        16,

      filter: [
        "in",

        [
          "get",
          "road_class",
        ],

        [
          "literal",
          roadClasses,
        ],
      ],

      layout: {
        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          lineColor,

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          minzoom,
          minimumWidth,

          14,
          maximumWidth,
        ],

        "line-opacity":
          0.92,
      },
    },
    beforeId
  );
}

function ensurePortugalRoadLineLayers(
  map,
  {
    reloadKey = "",
  } = {}
) {
  if (
    !map ||
    !map.isStyleLoaded()
  ) {
    return;
  }

  ensurePortugalRoadNetworkSource(
    map,
    {
      reloadKey,
    }
  );

  const beforeId =
    getFirstMapSymbolLayerId(
      map
    );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_MUNICIPAL_LAYER_ID,

      roadClasses: [
        "municipal",
      ],

      minzoom:
        13,

      lineColor:
        "#d8d4cc",

      minimumWidth:
        0.8,

      maximumWidth:
        1.5,

      beforeId,
    }
  );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_REGIONAL_LAYER_ID,

      roadClasses: [
        "regional",
      ],

      minzoom:
        11,

      lineColor:
        "#efc976",

      minimumWidth:
        1.1,

      maximumWidth:
        2.2,

      beforeId,
    }
  );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_NATIONAL_LAYER_ID,

      roadClasses: [
        "national",
      ],

      /*
       * National roads are progressively included
       * by the PMTiles source:
       *
       * zoom 7: main national roads
       * zoom 8: secondary national roads
       * zoom 9: remaining national roads
       */
      minzoom:
        7,

      lineColor:
        "#efb84f",

      minimumWidth:
        1.35,

      maximumWidth:
        3.3,

      beforeId,
    }
  );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_PRINCIPAL_LAYER_ID,

      roadClasses: [
        "principal",
      ],

      minzoom:
        7,

      lineColor:
        "#e9a23b",

      minimumWidth:
        2,

      maximumWidth:
        4,

      beforeId,
    }
  );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_MOTORWAY_CASING_LAYER_ID,

      roadClasses: [
        "motorway",
        "european",
      ],

      minzoom:
        5,

      lineColor:
        "#fff4df",

      minimumWidth:
        3.8,

      maximumWidth:
        7.2,

      beforeId,
    }
  );

  addRoadNetworkLineLayer(
    map,
    {
      id:
        ROAD_LINE_MOTORWAY_LAYER_ID,

      roadClasses: [
        "motorway",
        "european",
      ],

      minzoom:
        5,

      lineColor:
        "#e69532",

      minimumWidth:
        2.4,

      maximumWidth:
        5.4,

      beforeId,
    }
  );
}
function hideBaseRoadShieldLayers(
  map
) {
  BASE_ROAD_SHIELD_LAYER_IDS.forEach(
    (
      layerId
    ) => {
      if (
        !map.getLayer(
          layerId
        )
      ) {
        return;
      }

      try {
        map.setLayoutProperty(
          layerId,
          "visibility",
          "none"
        );
      } catch (
        error
      ) {
        console.warn(
          `[EditableShipmentRouteMap] Could not hide base road-shield layer "${layerId}":`,
          error
        );
      }
    }
  );
}

function getBaseTransportationSourceId(
  map
) {
  const style =
    map?.getStyle?.();

  const layers =
    Array.isArray(
      style?.layers
    )
      ? style.layers
      : [];

  const transportationLayer =
    layers.find(
      (
        layer
      ) =>
        layer?.source &&
        layer[
          "source-layer"
        ] ===
          "transportation"
    );

  return (
    transportationLayer
      ?.source ||
    null
  );
}

function ensureEuropeMotorwayLineLayers(
  map
) {
  if (!map) {
    return;
  }

  ensureEuropeMotorwaySource(
    map
  );

  const beforeId =
    getFirstMapSymbolLayerId(
      map
    );

  if (
    !map.getLayer(
      EUROPE_MOTORWAY_CASING_LAYER_ID
    )
  ) {
    map.addLayer(
      {
        id:
          EUROPE_MOTORWAY_CASING_LAYER_ID,

        type:
          "line",

        source:
          EUROPE_MOTORWAY_SOURCE_ID,

        "source-layer":
          EUROPE_MOTORWAY_SOURCE_LAYER,

        minzoom:
          5,

        filter: [
          "in",

          [
            "get",
            "highway",
          ],

          [
            "literal",
            [
              "motorway",
              "motorway_link",
            ],
          ],
        ],

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#fff4df",

          "line-width": [
            "interpolate",

            [
              "linear",
            ],

            [
              "zoom",
            ],

            5,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              2.8,

              3.8,
            ],

            14,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              5.6,

              7.2,
            ],
          ],

          "line-opacity":
            0.92,
        },
      },
      beforeId
    );
  }

  if (
    !map.getLayer(
      EUROPE_MOTORWAY_LINE_LAYER_ID
    )
  ) {
    map.addLayer(
      {
        id:
          EUROPE_MOTORWAY_LINE_LAYER_ID,

        type:
          "line",

        source:
          EUROPE_MOTORWAY_SOURCE_ID,

        "source-layer":
          EUROPE_MOTORWAY_SOURCE_LAYER,

        minzoom:
          5,

        filter: [
          "in",

          [
            "get",
            "highway",
          ],

          [
            "literal",
            [
              "motorway",
              "motorway_link",
            ],
          ],
        ],

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#e69532",

          "line-width": [
            "interpolate",

            [
              "linear",
            ],

            [
              "zoom",
            ],

            5,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              1.6,

              2.4,
            ],

            14,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              3.8,

              5.4,
            ],
          ],

          "line-opacity":
            0.92,
        },
      },
      beforeId
    );
  }
}

function getBaseTransportationNameSourceId(
  map
) {
  const style =
    map?.getStyle?.();

  const layers =
    Array.isArray(
      style?.layers
    )
      ? style.layers
      : [];

  const transportationLayer =
    layers.find(
      (
        layer
      ) =>
        layer?.source &&
        layer[
          "source-layer"
        ] ===
          "transportation_name"
    );

  return (
    transportationLayer
      ?.source ||
    null
  );
}

function ensureEuropeRoadShieldLayer(
  map
) {
  if (!map) {
    return;
  }

  const sourceId =
    getBaseTransportationNameSourceId(
      map
    );

  if (!sourceId) {
    console.warn(
      "[EditableShipmentRouteMap] transportation_name source not found."
    );

    return;
  }

  ensureRoadShieldImages(
    map
  );

  /*
   * Normal European road references can continue
   * coming from the basemap.
   *
   * Motorways are deliberately excluded here.
   *
   * The basemap can contain labels for motorway
   * alignments that are still under construction,
   * which is what caused the floating A-11 shields.
   */
  if (
    !map.getLayer(
      EUROPE_ROAD_SHIELD_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        EUROPE_ROAD_SHIELD_LAYER_ID,

      type:
        "symbol",

      source:
        sourceId,

      "source-layer":
        "transportation_name",

      minzoom:
        5,

      filter: [
        "all",

        [
          "any",

          [
            "has",
            "ref",
          ],

          [
            "has",
            "route_1_ref",
          ],
        ],

        [
          "==",

          [
            "geometry-type",
          ],

          "LineString",
        ],

        /*
         * IMPORTANT:
         * motorway references no longer come from
         * transportation_name.
         */
        [
          "!=",

          [
            "get",
            "class",
          ],

          "motorway",
        ],

        [
          "match",

          [
            "get",
            "network",
          ],

          [
            "us-highway",
            "us-interstate",
            "us-state",
          ],

          false,

          true,
        ],
      ],

      layout: {
        "symbol-placement":
          "line",

        "symbol-spacing":
          145,

        "symbol-avoid-edges":
          true,

        "symbol-sort-key": [
          "match",

          [
            "get",
            "class",
          ],

          "trunk",
          1,

          "primary",
          2,

          "secondary",
          3,

          4,
        ],

        "text-field": [
          "to-string",

          [
            "coalesce",

            [
              "get",
              "ref",
            ],

            [
              "get",
              "route_1_ref",
            ],

            "",
          ],
        ],

        "text-font": [
          "Noto Sans Regular",
        ],

        "text-size": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          6,
          9,

          8,
          10,

          10,
          11,

          12,
          11.5,

          15,
          12,
        ],

        "text-padding":
          2,

        "text-rotation-alignment":
          "viewport",

        "text-pitch-alignment":
          "viewport",

        "text-keep-upright":
          true,

        "text-allow-overlap":
          false,

        "text-ignore-placement":
          false,

        "icon-image": [
          "case",

          [
            "==",

            [
              "slice",

              [
                "upcase",

                [
                  "to-string",

                  [
                    "coalesce",

                    [
                      "get",
                      "ref",
                    ],

                    [
                      "get",
                      "route_1_ref",
                    ],

                    "",
                  ],
                ],
              ],

              0,
              1,
            ],

            "E",
          ],

          ROAD_SHIELD_EUROPEAN_ICON_ID,

          ROAD_SHIELD_MOTORWAY_ICON_ID,
        ],

        "icon-text-fit":
          "both",

        "icon-text-fit-padding": [
          1.5,
          4,
          1.5,
          4,
        ],

        "icon-padding":
          5,

        "icon-rotation-alignment":
          "viewport",

        "icon-pitch-alignment":
          "viewport",

        "icon-allow-overlap":
          false,

        "icon-ignore-placement":
          false,
      },

      paint: {
        "text-color":
          "#ffffff",

        "text-halo-width":
          0,
      },
    });
  }

  /*
   * Motorway shields now come from the exact same
   * V1 PMTiles archive as the orange motorway lines.
   *
   * Therefore an A-11 shield cannot continue beyond
   * the motorway geometry contained in V1.
   */
  ensureEuropeMotorwaySource(
    map
  );

  if (
    !map.getLayer(
      EUROPE_MOTORWAY_SHIELD_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        EUROPE_MOTORWAY_SHIELD_LAYER_ID,

      type:
        "symbol",

      source:
        EUROPE_MOTORWAY_SOURCE_ID,

      "source-layer":
        EUROPE_MOTORWAY_SOURCE_LAYER,

      minzoom:
        5,

      maxzoom:
        16,

      filter: [
        "all",

        [
          "==",

          [
            "get",
            "highway",
          ],

          "motorway",
        ],

        [
          "has",
          "ref",
        ],
      ],

      layout: {
        "symbol-placement":
          "line",

        "symbol-spacing":
          145,

        "symbol-avoid-edges":
          true,

        "text-field": [
          "to-string",

          [
            "get",
            "ref",
          ],
        ],

        "text-font": [
          "Noto Sans Regular",
        ],

        "text-size": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          6,
          9,

          8,
          10,

          10,
          11,

          12,
          11.5,

          15,
          12,
        ],

        "text-padding":
          2,

        "text-rotation-alignment":
          "viewport",

        "text-pitch-alignment":
          "viewport",

        "text-keep-upright":
          true,

        "text-allow-overlap":
          false,

        "text-ignore-placement":
          false,

        "icon-image": [
          "case",

          /*
           * E-routes keep their green shield.
           */
          [
            "==",

            [
              "slice",

              [
                "upcase",

                [
                  "to-string",

                  [
                    "get",
                    "ref",
                  ],
                ],
              ],

              0,
              1,
            ],

            "E",
          ],

          ROAD_SHIELD_EUROPEAN_ICON_ID,

          /*
           * Normal motorway references such as
           * A-11 use the blue shield.
           */
          ROAD_SHIELD_MOTORWAY_ICON_ID,
        ],

        "icon-text-fit":
          "both",

        "icon-text-fit-padding": [
          1.5,
          4,
          1.5,
          4,
        ],

        "icon-padding":
          5,

        "icon-rotation-alignment":
          "viewport",

        "icon-pitch-alignment":
          "viewport",

        "icon-allow-overlap":
          false,

        "icon-ignore-placement":
          false,
      },

      paint: {
        "text-color":
          "#ffffff",

        "text-halo-width":
          0,
      },
    });
  }
}

function ensurePortugalRoadShieldLayers(
  map,
  {
    reloadKey = "",
  } = {}
) {
  /*
   * Do not use map.isStyleLoaded() here.
   *
   * This function runs after road and route layers
   * have been added or moved. Those style mutations
   * temporarily make isStyleLoaded() return false,
   * even though the style already exists and can
   * accept images and layers.
   */
  const currentStyle =
    map?.getStyle?.();

  if (
    !map ||
    !currentStyle ||
    !Array.isArray(
      currentStyle.layers
    )
  ) {
    return;
  }

  /*
   * The base style already contains generic white
   * road-reference shields. They reserve symbol
   * collision space and can suppress our custom
   * Portuguese blue motorway shields.
   */
  hideBaseRoadShieldLayers(
    map
  );

  ensurePortugalRoadNetworkSource(
    map,
    {
      reloadKey,
    }
  );

  ensureRoadShieldImages(
    map
  );

  addRoadShieldLayer(
    map
  );

  ensureEuropeRoadShieldLayer(
    map
  );

  if (
    !map.getLayer(
      ROAD_SHIELD_ALL_LAYER_ID
    )
  ) {
    return;
  }

  try {
    if (
      map.getLayer(
        EUROPE_ROAD_SHIELD_LAYER_ID
      )
    ) {
      /*
       * Portugal is deliberately immediately before
       * the European fallback.
       *
       * The exact Portuguese PMTiles anchors therefore
       * keep priority where both datasets contain the
       * same road reference.
       */
      map.moveLayer(
        ROAD_SHIELD_ALL_LAYER_ID,
        EUROPE_ROAD_SHIELD_LAYER_ID
      );
    } else {
      map.moveLayer(
        ROAD_SHIELD_ALL_LAYER_ID
      );
    }
  } catch (
    error
  ) {
    console.warn(
      "[EditableShipmentRouteMap] Could not order road-shield layers:",
      error
    );
  }
}

function buildVehicleRestrictionFilter(
  truck
) {
  const height =
    Number(
      truck?.height
    );

  const width =
    Number(
      truck?.width
    );

  const length =
    Number(
      truck?.length
    );

  const weight =
    Number(
      truck?.weight
    );

  const filters = [
    [
      "in",

      [
        "get",
        "hgv",
      ],

      [
        "literal",
        [
          "no",
          "private",
        ],
      ],
    ],

    [
      "in",

      [
        "get",
        "goods",
      ],

      [
        "literal",
        [
          "no",
          "private",
        ],
      ],
    ],

    [
      "in",

      [
        "get",
        "motor_vehicle",
      ],

      [
        "literal",
        [
          "no",
          "private",
        ],
      ],
    ],

    [
      "in",

      [
        "get",
        "access",
      ],

      [
        "literal",
        [
          "no",
          "private",
        ],
      ],
    ],
  ];

  if (
    Number.isFinite(
      weight
    ) &&
    weight >
      0
  ) {
    filters.push([
      "all",

      [
        "has",
        "maxweight",
      ],

      [
        ">",
        weight,

        [
          "get",
          "maxweight",
        ],
      ],
    ]);
  }

  if (
    Number.isFinite(
      height
    ) &&
    height >
      0
  ) {
    filters.push([
      "all",

      [
        "has",
        "maxheight",
      ],

      [
        ">",
        height,

        [
          "get",
          "maxheight",
        ],
      ],
    ]);
  }

  if (
    Number.isFinite(
      width
    ) &&
    width >
      0
  ) {
    filters.push([
      "all",

      [
        "has",
        "maxwidth",
      ],

      [
        ">",
        width,

        [
          "get",
          "maxwidth",
        ],
      ],
    ]);
  }

  if (
    Number.isFinite(
      length
    ) &&
    length >
      0
  ) {
    filters.push([
      "all",

      [
        "has",
        "maxlength",
      ],

      [
        ">",
        length,

        [
          "get",
          "maxlength",
        ],
      ],
    ]);
  }

  return [
    "any",
    ...filters,
  ];
}

function buildConditionalVehicleRestrictionFilter() {
  return [
    "any",

    [
      "in",

      [
        "get",
        "hgv",
      ],

      [
        "literal",
        [
          "destination",
          "delivery",
          "customers",
          "permit",
        ],
      ],
    ],

    [
      "has",
      "hgv_conditional",
    ],

    [
      "has",
      "access_conditional",
    ],
  ];
}

function ensureVehicleRestrictionLayers(
  map,
  {
    truck,
    visible,
  }
) {
  if (
    !map ||
    !map.getSource(
      ROAD_SHIELDS_SOURCE_ID
    )
  ) {
    return;
  }

  const visibility =
    visible
      ? "visible"
      : "none";

  const hardFilter =
    buildVehicleRestrictionFilter(
      truck
    );

  const conditionalFilter =
    buildConditionalVehicleRestrictionFilter();

  if (
    !map.getLayer(
      ROAD_RESTRICTION_CASING_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        ROAD_RESTRICTION_CASING_LAYER_ID,

      type:
        "line",

      source:
        ROAD_SHIELDS_SOURCE_ID,

      "source-layer":
        ROAD_RESTRICTIONS_SOURCE_LAYER,

      minzoom:
        11,

      maxzoom:
        16,

      filter:
        hardFilter,

      layout: {
        visibility,

        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          "#ffffff",

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          11,
          5,

          14,
          9,
        ],

        "line-opacity":
          0.96,
      },
    });
  }

  if (
    !map.getLayer(
      ROAD_RESTRICTION_HARD_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        ROAD_RESTRICTION_HARD_LAYER_ID,

      type:
        "line",

      source:
        ROAD_SHIELDS_SOURCE_ID,

      "source-layer":
        ROAD_RESTRICTIONS_SOURCE_LAYER,

      minzoom:
        11,

      maxzoom:
        16,

      filter:
        hardFilter,

      layout: {
        visibility,

        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          "#ef4444",

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          11,
          2.8,

          14,
          5.5,
        ],

        "line-opacity":
          0.92,
      },
    });
  }

  if (
    !map.getLayer(
      ROAD_RESTRICTION_CONDITIONAL_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        ROAD_RESTRICTION_CONDITIONAL_LAYER_ID,

      type:
        "line",

      source:
        ROAD_SHIELDS_SOURCE_ID,

      "source-layer":
        ROAD_RESTRICTIONS_SOURCE_LAYER,

      minzoom:
        11,

      maxzoom:
        16,

      filter:
        conditionalFilter,

      layout: {
        visibility,

        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          "#f59e0b",

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          11,
          2.5,

          14,
          5,
        ],

        "line-dasharray": [
          2,
          1.5,
        ],

        "line-opacity":
          0.88,
      },
    });
  }

  [
    ROAD_RESTRICTION_CASING_LAYER_ID,
    ROAD_RESTRICTION_HARD_LAYER_ID,
  ].forEach(
    (
      layerId
    ) => {
      if (
        !map.getLayer(
          layerId
        )
      ) {
        return;
      }

      map.setFilter(
        layerId,
        hardFilter
      );

      map.setLayoutProperty(
        layerId,
        "visibility",
        visibility
      );
    }
  );

  if (
    map.getLayer(
      ROAD_RESTRICTION_CONDITIONAL_LAYER_ID
    )
  ) {
    map.setFilter(
      ROAD_RESTRICTION_CONDITIONAL_LAYER_ID,
      conditionalFilter
    );

    map.setLayoutProperty(
      ROAD_RESTRICTION_CONDITIONAL_LAYER_ID,
      "visibility",
      visibility
    );
  }
}

function buildRejectedRoadFeatureCollection(
  locations
) {
  const features =
    (
      Array.isArray(
        locations
      )
        ? locations
        : []
    )
      .filter(
        (
          location
        ) =>
          location
            ?.accessible ===
          false
      )
      .map(
        (
          location,
          index
        ) => {
          const normalizedRoadGeometry =
            normalizeGeometry(
              location
                ?.roadGeometry
            );

          if (
            !normalizedRoadGeometry
              ?.geometry
          ) {
            return null;
          }

          return {
            type:
              "Feature",

            geometry:
              normalizedRoadGeometry
                .geometry,

            properties: {
              index,

              wayId:
                location?.wayId ??
                null,

              roadName:
                location
                  ?.roadName ||
                null,
            },
          };
        }
      )
      .filter(
        Boolean
      );

  return {
    type:
      "FeatureCollection",

    features,
  };
}

function getFirstRejectedLocation(
  locations
) {
  return (
    (
      Array.isArray(
        locations
      )
        ? locations
        : []
    ).find(
      (
        location
      ) => {
        if (
          location
            ?.accessible !==
          false
        ) {
          return false;
        }

        const latitude =
          Number(
            location
              ?.correlatedLatitude ??
            location
              ?.latitude
          );

        const longitude =
          Number(
            location
              ?.correlatedLongitude ??
            location
              ?.longitude
          );

        return (
          Number.isFinite(
            latitude
          ) &&
          Number.isFinite(
            longitude
          )
        );
      }
    ) ||
    null
  );
}

function ensureRejectedRoadHighlightLayers(
  map,
  {
    data,
  }
) {
  if (
    !map
  ) {
    return;
  }

  const rejectedRoadData =
    data?.type ===
      "FeatureCollection" &&
    Array.isArray(
      data?.features
    )
      ? data
      : getEmptyFeatureCollection();

  const visibility =
    rejectedRoadData
      .features
      .length >
    0
      ? "visible"
      : "none";

  let rejectedRoadSource =
    map.getSource(
      REJECTED_ROAD_SOURCE_ID
    );

  if (
    !rejectedRoadSource
  ) {
    try {
      map.addSource(
        REJECTED_ROAD_SOURCE_ID,
        {
          type:
            "geojson",

          data:
            rejectedRoadData,
        }
      );
    } catch (
      error
    ) {
      console.warn(
        "[EditableShipmentRouteMap] Could not add rejected-road source:",
        error
      );

      return;
    }

    rejectedRoadSource =
      map.getSource(
        REJECTED_ROAD_SOURCE_ID
      );
  }

  if (
    rejectedRoadSource &&
    typeof rejectedRoadSource
      .setData ===
      "function"
  ) {
    rejectedRoadSource.setData(
      rejectedRoadData
    );
  }

  if (
    !map.getLayer(
      REJECTED_ROAD_GLOW_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        REJECTED_ROAD_GLOW_LAYER_ID,

      type:
        "line",

      source:
        REJECTED_ROAD_SOURCE_ID,

      minzoom:
        0,

      maxzoom:
        24,

      layout: {
        visibility,

        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          "#fecaca",

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          8,
          8,

          14,
          18,
        ],

        "line-blur":
          1.8,

        "line-opacity":
          0.96,
      },
    });
  }

  if (
    !map.getLayer(
      REJECTED_ROAD_LINE_LAYER_ID
    )
  ) {
    map.addLayer({
      id:
        REJECTED_ROAD_LINE_LAYER_ID,

      type:
        "line",

      source:
        REJECTED_ROAD_SOURCE_ID,

      minzoom:
        0,

      maxzoom:
        24,

      layout: {
        visibility,

        "line-cap":
          "round",

        "line-join":
          "round",
      },

      paint: {
        "line-color":
          "#dc2626",

        "line-width": [
          "interpolate",

          [
            "linear",
          ],

          [
            "zoom",
          ],

          8,
          4,

          14,
          8,
        ],

        "line-opacity":
          1,
      },
    });
  }

  [
    REJECTED_ROAD_GLOW_LAYER_ID,
    REJECTED_ROAD_LINE_LAYER_ID,
  ].forEach(
    (
      layerId
    ) => {
      if (
        !map.getLayer(
          layerId
        )
      ) {
        return;
      }

      map.setLayoutProperty(
        layerId,
        "visibility",
        visibility
      );

      try {
        map.moveLayer(
          layerId
        );
      } catch {}
    }
  );
}

function normalizeGeometry(
  value
) {
  if (
    value?.type ===
      "Feature" &&
    value?.geometry?.type ===
      "LineString" &&
    Array.isArray(
      value.geometry.coordinates
    )
  ) {
    return value;
  }

  if (
    value?.type ===
      "LineString" &&
    Array.isArray(
      value.coordinates
    )
  ) {
    return {
      type:
        "Feature",

      geometry:
        value,

      properties:
        {},
    };
  }

  return null;
}

function normalizePoints(
  points
) {
  return (
    Array.isArray(
      points
    )
      ? points
      : []
  )
    .map(
      (
        point,
        index
      ) => {
        const lat =
          Number(
            point?.lat ??
              point?.latitude
          );

        const lng =
          Number(
            point?.lng ??
              point?.lon ??
              point?.longitude
          );

        if (
          !Number.isFinite(
            lat
          ) ||
          !Number.isFinite(
            lng
          ) ||
          Math.abs(
            lat
          ) >
            90 ||
          Math.abs(
            lng
          ) >
            180
        ) {
          return null;
        }

        return {
          ...point,

          id:
            String(
              point?.id ||
                `route-point-${index}`
            ),

          sequence:
            Number.isFinite(
              Number(
                point?.sequence
              )
            )
              ? Number(
                  point.sequence
                )
              : index,

          lat,
          lng,
        };
      }
    )
    .filter(
      Boolean
    )
    .sort(
      (
        pointA,
        pointB
      ) =>
        pointA.sequence -
        pointB.sequence
    );
}

function createMarkerElement({
  point,
  index,
  editable,
}) {
  const element =
    document.createElement(
      "div"
    );

  const pointType =
    String(
      point?.type ||
        ""
    ).toUpperCase();

  element.className = [
    "editable-shipment-route-marker",

    pointType ===
    "PICKUP"
      ? "is-pickup"
      : pointType ===
          "VIA"
        ? "is-via"
        : "is-delivery",

    editable
      ? "is-editable"
      : "",
  ]
    .filter(
      Boolean
    )
    .join(
      " "
    );

  element.textContent =
    String(
      point?.markerLabel ||
        (
          pointType ===
          "VIA"
            ? "•"
            : index +
              1
        )
    );

  element.title =
    point?.name ||
    point?.placeName ||
    point?.location ||
    `Paragem ${index + 1}`;

  return element;
}

function createRouteViaPreviewElement() {
  const element =
    document.createElement(
      "div"
    );

  element.className =
    "editable-shipment-route-via-preview";

  element.textContent =
    "•";

  return element;
}

function getPointToSegmentDistance(
  point,
  segmentStart,
  segmentEnd
) {
  const segmentX =
    segmentEnd.x -
    segmentStart.x;

  const segmentY =
    segmentEnd.y -
    segmentStart.y;

  const segmentLengthSquared =
    segmentX *
      segmentX +
    segmentY *
      segmentY;

  if (
    segmentLengthSquared ===
    0
  ) {
    return Math.hypot(
      point.x -
        segmentStart.x,
      point.y -
        segmentStart.y
    );
  }

  const projection =
    Math.max(
      0,
      Math.min(
        1,
        (
          (
            point.x -
            segmentStart.x
          ) *
            segmentX +
          (
            point.y -
            segmentStart.y
          ) *
            segmentY
        ) /
          segmentLengthSquared
      )
    );

  const projectedX =
    segmentStart.x +
    projection *
      segmentX;

  const projectedY =
    segmentStart.y +
    projection *
      segmentY;

  return Math.hypot(
    point.x -
      projectedX,
    point.y -
      projectedY
  );
}

function getNearestOrderedPointSegmentIndex(
  map,
  points,
  lngLat
) {
  if (
    !map ||
    !lngLat ||
    !Array.isArray(
      points
    ) ||
    points.length <
      2
  ) {
    return -1;
  }

  const projectedPoint =
    map.project([
      lngLat.lng,
      lngLat.lat,
    ]);

  let nearestSegmentIndex =
    -1;

  let nearestDistance =
    Number.POSITIVE_INFINITY;

  for (
    let index =
      0;
    index <
    points.length -
      1;
    index +=
      1
  ) {
    const startPoint =
      map.project([
        points[index].lng,
        points[index].lat,
      ]);

    const endPoint =
      map.project([
        points[index + 1].lng,
        points[index + 1].lat,
      ]);

    const distance =
      getPointToSegmentDistance(
        projectedPoint,
        startPoint,
        endPoint
      );

    if (
      distance <
      nearestDistance
    ) {
      nearestDistance =
        distance;

      nearestSegmentIndex =
        index;
    }
  }

  return nearestSegmentIndex;
}



function getEmptyFeatureCollection() {
  return {
    type:
      "FeatureCollection",

    features:
      [],
  };
}


function parseMapSearchCoordinates(
  value
) {
  const cleanedValue =
    String(
      value ||
        ""
    )
      .trim()
      .replace(
        /^\(|\)$/g,
        ""
      );

  const dotDecimalMatch =
    cleanedValue.match(
      /^(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/
    );

  const commaDecimalMatch =
    cleanedValue.match(
      /^(-?\d{1,2}(?:,\d+)?)\s*;\s*(-?\d{1,3}(?:,\d+)?)$/
    );

  const match =
    dotDecimalMatch ||
    commaDecimalMatch;

  if (
    !match
  ) {
    return null;
  }

  const lat =
    Number(
      String(
        match[1]
      ).replace(
        ",",
        "."
      )
    );

  const lng =
    Number(
      String(
        match[2]
      ).replace(
        ",",
        "."
      )
    );

  if (
    !Number.isFinite(
      lat
    ) ||
    !Number.isFinite(
      lng
    ) ||
    Math.abs(
      lat
    ) >
      90 ||
    Math.abs(
      lng
    ) >
      180
  ) {
    return null;
  }

  return {
    lat,
    lng,
  };
}

function getMapSearchResultCoordinates(
  item
) {
  const directLatitude =
    Number(
      item?.lat ??
        item?.latitude
    );

  const directLongitude =
    Number(
      item?.lng ??
        item?.lon ??
        item?.longitude
    );

  if (
    Number.isFinite(
      directLatitude
    ) &&
    Number.isFinite(
      directLongitude
    )
  ) {
    return {
      lat:
        directLatitude,

      lng:
        directLongitude,
    };
  }

  if (
    Array.isArray(
      item?.center
    ) &&
    item.center.length >=
      2
  ) {
    const [
      longitude,
      latitude,
    ] =
      item.center;

    if (
      Number.isFinite(
        Number(
          latitude
        )
      ) &&
      Number.isFinite(
        Number(
          longitude
        )
      )
    ) {
      return {
        lat:
          Number(
            latitude
          ),

        lng:
          Number(
            longitude
          ),
      };
    }
  }

  if (
    item?.coordinates &&
    typeof item.coordinates ===
      "object"
  ) {
    const objectLatitude =
      Number(
        item.coordinates.lat ??
          item.coordinates.latitude
      );

    const objectLongitude =
      Number(
        item.coordinates.lng ??
          item.coordinates.lon ??
          item.coordinates.longitude
      );

    if (
      Number.isFinite(
        objectLatitude
      ) &&
      Number.isFinite(
        objectLongitude
      )
    ) {
      return {
        lat:
          objectLatitude,

        lng:
          objectLongitude,
      };
    }
  }

  return parseMapSearchCoordinates(
    item?.coordinates
  );
}

function getMapSearchResultTitle(
  item
) {
  return String(
    item?.name ||
      item?.label ||
      item?.placeName ||
      item?.displayName ||
      item?.display_name ||
      item?.address ||
      "Local encontrado"
  ).trim();
}

function getMapSearchResultSubtitle(
  item
) {
  const title =
    getMapSearchResultTitle(
      item
    );

  const displayName =
    String(
      item?.displayName ||
        item?.display_name ||
        ""
    ).trim();

  if (
    displayName &&
    displayName !==
      title
  ) {
    return displayName;
  }

  return [
    item?.address,
    item?.postalCode ||
      item?.postcode,
    item?.city ||
      item?.town ||
      item?.village,
    item?.country,
  ]
    .filter(
      Boolean
    )
    .join(
      ", "
    );
}

function createCoordinateSearchResult({
  lat,
  lng,
}) {
  const coordinateLabel =
    `${lat.toFixed(
      6
    )}, ${lng.toFixed(
      6
    )}`;

  return {
    id:
      `map-coordinate-${lat}-${lng}`,

    name:
      coordinateLabel,

    displayName:
      "Coordenadas introduzidas",

    lat,

    lng,

    latitude:
      lat,

    longitude:
      lng,

    coordinates:
      coordinateLabel,
  };
}

export default function EditableShipmentRouteMap({
  points = [],
  editable = false,
  showSearch = false,
  avoidTolls = false,
  apiBaseUrl = "/api",
  authToken = "",
  truck = {},
  pickingPointId = "",
  showVehicleRestrictions = false,
  onPointMoved,
  onPointPicked,
  onSearchResultSelected,
  onRouteViaPointCreated,
  onRouteViaPointRemoved,
  onRouteChange,
  onRouteValidationChange,
  onError,
}) {
  const mapContainerRef =
    useRef(
      null
    );

  const mapRef =
    useRef(
      null
    );

  const searchContainerRef =
    useRef(
      null
    );

  const searchMarkerRef =
    useRef(
      null
    );

  const searchRequestControllerRef =
    useRef(
      null
    );

  const routeRequestIdRef =
    useRef(
      0
    );

  const activeRouteCacheKeyRef =
    useRef(
      ""
    );

  const markersRef =
    useRef(
      []
    );

  const onPointMovedRef =
    useRef(
      onPointMoved
    );

  const onPointPickedRef =
    useRef(
      onPointPicked
    );

  const onRouteViaPointCreatedRef =
    useRef(
      onRouteViaPointCreated
    );

  const onRouteViaPointRemovedRef =
    useRef(
      onRouteViaPointRemoved
    );

  const routeLineDragStateRef =
    useRef(
      null
    );

  const routeLinePreviewMarkerRef =
    useRef(
      null
    );

  const previewRouteTimerRef =
    useRef(
      null
    );

  const previewRouteControllerRef =
    useRef(
      null
    );

  const previewRouteLatestPointsRef =
    useRef(
      []
    );

  const previewRouteLastRequestAtRef =
    useRef(
      0
    );

  const previewRouteRequestIdRef =
    useRef(
      0
    );

  const previewRouteBaseSignatureRef =
    useRef(
      ""
    );

  const previewRouteAwaitingCommitRef =
    useRef(
      false
    );

  const previewRouteTargetCacheKeyRef =
    useRef(
      ""
    );

  const previousEditableRef =
    useRef(
      editable
    );

  const onRouteChangeRef =
    useRef(
      onRouteChange
    );

  const onRouteValidationChangeRef =
    useRef(
      onRouteValidationChange
    );

  const onErrorRef =
    useRef(
      onError
    );

  const lastFitPointIdsRef =
    useRef(
      ""
    );

  const roadNetworkRetryAttemptRef =
    useRef(
      0
    );

  const roadNetworkRetryTimerRef =
    useRef(
      null
    );

  const [
    roadNetworkReloadKey,
    setRoadNetworkReloadKey,
  ] =
    useState(
      ""
    );

  const [
    mapReady,
    setMapReady,
  ] =
    useState(
      false
    );

  const [
    routeResult,
    setRouteResult,
  ] =
    useState(
      null
    );

  const [
    rejectedRoadData,
    setRejectedRoadData,
  ] =
    useState(
      getEmptyFeatureCollection()
    );

  const [
    previewRouteResult,
    setPreviewRouteResult,
  ] =
    useState(
      null
    );

  const [
    isCalculatingPreviewRoute,
    setIsCalculatingPreviewRoute,
  ] =
    useState(
      false
    );

  const [
    resolvedRouteCacheKey,
    setResolvedRouteCacheKey,
  ] =
    useState(
      ""
    );

  const [
    isCalculatingRoute,
    setIsCalculatingRoute,
  ] =
    useState(
      false
    );

  const [
    mapError,
    setMapError,
  ] =
    useState(
      ""
    );

  const [
    mapErrorKind,
    setMapErrorKind,
  ] =
    useState(
      "GENERIC"
    );

  const [
    isMapErrorToastVisible,
    setIsMapErrorToastVisible,
  ] =
    useState(
      false
    );

  const [
    isPickingLocation,
    setIsPickingLocation,
  ] =
    useState(
      false
    );

  const [
    mapSearchText,
    setMapSearchText,
  ] =
    useState(
      ""
    );

  const [
    mapSearchResults,
    setMapSearchResults,
  ] =
    useState(
      []
    );

  const [
    mapSearchError,
    setMapSearchError,
  ] =
    useState(
      ""
    );

  const [
    isMapSearching,
    setIsMapSearching,
  ] =
    useState(
      false
    );

  const [
    isMapSearchOpen,
    setIsMapSearchOpen,
  ] =
    useState(
      false
    );

    const [
      isDraggingRoute,
      setIsDraggingRoute,
    ] =
      useState(
        false
      );

  const orderedPoints =
    useMemo(
      () =>
        normalizePoints(
          points
        ),
      [
        points,
      ]
    );

  const tollVehicleClass =
    useMemo(
      () => {
        const explicitVehicleClass =
          Number(
            truck?.tollVehicleClass ??
              truck?.vehicleTollClass ??
              truck?.tollClass
          );

        if (
          [
            1,
            2,
            3,
            4,
          ].includes(
            explicitVehicleClass
          )
        ) {
          return explicitVehicleClass;
        }

        const heightAboveFirstAxle =
          Number(
            truck?.heightAboveFirstAxle ??
              truck?.vehicleHeightAboveFirstAxle
          );

        if (
          Number.isFinite(
            heightAboveFirstAxle
          ) &&
          heightAboveFirstAxle >
            0 &&
          heightAboveFirstAxle <
            1.1
        ) {
          return 1;
        }

        const axleCount =
          Number(
            truck?.axleCount ??
              truck?.axle_count
          );

        if (
          !Number.isFinite(
            axleCount
          ) ||
          axleCount <=
            0
        ) {
          return 4;
        }

        if (
          axleCount <=
          2
        ) {
          return 2;
        }

        if (
          axleCount ===
          3
        ) {
          return 3;
        }

        return 4;
      },
      [
        truck?.axleCount,
        truck?.axle_count,
        truck?.heightAboveFirstAxle,
        truck?.tollClass,
        truck?.tollVehicleClass,
        truck?.vehicleHeightAboveFirstAxle,
        truck?.vehicleTollClass,
      ]
    );

  const tollTravelDate =
    useMemo(
      () => {
        const datedPickup =
          orderedPoints.find(
            (
              point
            ) =>
              String(
                point?.type ||
                  point?.pointKind ||
                  ""
              )
                .trim()
                .toUpperCase() ===
                "PICKUP" &&
              Boolean(
                point?.date
              )
          );

        const datedPoint =
          datedPickup ||
          orderedPoints.find(
            (
              point
            ) =>
              Boolean(
                point?.date
              )
          );

        const rawDate =
          String(
            datedPoint?.date ||
              ""
          ).trim();

        const isoDateMatch =
          rawDate.match(
            /^(\d{4})-(\d{2})-(\d{2})/
          );

        if (
          isoDateMatch
        ) {
          return [
            isoDateMatch[1],
            isoDateMatch[2],
            isoDateMatch[3],
          ].join(
            "-"
          );
        }

        const portugueseDateMatch =
          rawDate.match(
            /^(\d{2})[/-](\d{2})[/-](\d{4})/
          );

        if (
          portugueseDateMatch
        ) {
          return [
            portugueseDateMatch[3],
            portugueseDateMatch[2],
            portugueseDateMatch[1],
          ].join(
            "-"
          );
        }

        const today =
          new Date();

        return [
          today.getFullYear(),

          String(
            today.getMonth() +
              1
          ).padStart(
            2,
            "0"
          ),

          String(
            today.getDate()
          ).padStart(
            2,
            "0"
          ),
        ].join(
          "-"
        );
      },
      [
        orderedPoints,
      ]
    );

  const routeCacheKey =
    useMemo(
      () => {
        if (
          orderedPoints.length <
          2
        ) {
          return "";
        }

        const coordinateSignature =
          orderedPoints
            .map(
              (
                point,
                index
              ) => {
                const latitude =
                  Number(
                    point?.lat ??
                    point?.latitude
                  );

                const longitude =
                  Number(
                    point?.lng ??
                    point?.lon ??
                    point?.longitude
                  );

                if (
                  !Number.isFinite(
                    latitude
                  ) ||
                  !Number.isFinite(
                    longitude
                  )
                ) {
                  return `${index}:invalid`;
                }

                return [
                  index,
                  latitude.toFixed(
                    7
                  ),
                  longitude.toFixed(
                    7
                  ),
                ].join(
                  ":"
                );
              }
            )
            .join(
              "|"
            );

        const truckSignature =
          [
            truck?.height,
            truck?.width,
            truck?.length,
            truck?.weight,
            truck?.axleCount ??
              truck?.axle_count,
          ]
            .map(
              (
                value
              ) => {
                const numericValue =
                  Number(
                    value
                  );

                return Number.isFinite(
                  numericValue
                )
                  ? numericValue.toFixed(
                      3
                    )
                  : "0.000";
              }
            )
            .join(
              ":"
            );

        return [
          "truck",
          avoidTolls
            ? "avoid-tolls"
            : "allow-tolls",
          truckSignature,
          coordinateSignature,
        ].join(
          "|"
        );
      },
      [
        orderedPoints,
        avoidTolls,
        tollTravelDate,
        tollVehicleClass,
        truck?.axleCount,
        truck?.axle_count,
        truck?.height,
        truck?.length,
        truck?.weight,
        truck?.width,
      ]
    );

    const clearLiveRoutePreview =
    useCallback(
      () => {
        if (
          previewRouteTimerRef.current !==
          null
        ) {
          window.clearTimeout(
            previewRouteTimerRef.current
          );

          previewRouteTimerRef.current =
            null;
        }

        previewRouteControllerRef
          .current
          ?.abort();

        previewRouteControllerRef.current =
          null;

        previewRouteLatestPointsRef.current =
          [];

        previewRouteRequestIdRef.current +=
          1;

        setPreviewRouteResult(
          null
        );

        setIsCalculatingPreviewRoute(
          false
        );
      },
      []
    );

  const queueLiveRoutePreview =
    useCallback(
      (
        nextPoints
      ) => {
        const normalizedPreviewPoints =
          normalizePoints(
            nextPoints
          );

        if (
          normalizedPreviewPoints.length <
          2
        ) {
          return;
        }

        previewRouteLatestPointsRef.current =
          normalizedPreviewPoints;

        /*
         * Do not reset an existing timer.
         * The most recent coordinates are kept in the ref,
         * giving us a throttled request instead of a debounce.
         */
        if (
          previewRouteTimerRef.current !==
          null
        ) {
          return;
        }

        const elapsedSinceLastRequest =
          Date.now() -
          previewRouteLastRequestAtRef.current;

        const requestDelay =
          Math.max(
            0,
            180 -
              elapsedSinceLastRequest
          );

        previewRouteTimerRef.current =
          window.setTimeout(
            async () => {
              previewRouteTimerRef.current =
                null;

              const pointsForRequest =
                normalizePoints(
                  previewRouteLatestPointsRef.current
                );

              if (
                pointsForRequest.length <
                2
              ) {
                return;
              }

              const previewRequestId =
                previewRouteRequestIdRef.current +
                1;

              previewRouteRequestIdRef.current =
                previewRequestId;

              previewRouteControllerRef
                .current
                ?.abort();

              const controller =
                new AbortController();

              previewRouteControllerRef.current =
                controller;

              previewRouteLastRequestAtRef.current =
                Date.now();

              setIsCalculatingPreviewRoute(
                true
              );

              try {
                const endpoint =
                  `${String(
                    apiBaseUrl
                  ).replace(
                    /\/$/,
                    ""
                  )}/maps/route`;

                const truckOptions = {
                  height:
                    Number(
                      truck?.height
                    ) ||
                    undefined,

                  width:
                    Number(
                      truck?.width
                    ) ||
                    undefined,

                  length:
                    Number(
                      truck?.length
                    ) ||
                    undefined,

                  weight:
                    Number(
                      truck?.weight
                    ) ||
                    undefined,

                  axleCount:
                    Number(
                      truck?.axleCount ??
                        truck?.axle_count
                    ) ||
                    undefined,
                };

                Object.keys(
                  truckOptions
                ).forEach(
                  (
                    key
                  ) => {
                    if (
                      truckOptions[
                        key
                      ] ===
                      undefined
                    ) {
                      delete truckOptions[
                        key
                      ];
                    }
                  }
                );

                const headers = {
                  Accept:
                    "application/json",

                  "Content-Type":
                    "application/json",
                };

                if (
                  authToken
                ) {
                  headers.Authorization =
                    `Bearer ${authToken}`;
                }

                const response =
                  await fetch(
                    endpoint,
                    {
                      method:
                        "POST",

                      signal:
                        controller.signal,

                      headers,

                      body:
                        JSON.stringify({
                          locations:
                            pointsForRequest.map(
                              (
                                point
                              ) => ({
                                lat:
                                  point.lat,

                                lon:
                                  point.lng,
                              })
                            ),

                          costing:
                            "truck",

                          truck:
                            truckOptions,

                          avoidTolls:
                            Boolean(
                              avoidTolls
                            ),

                          includeTollEstimate:
                            true,

                          tollVehicleClass,

                          tollTravelDate,

                          validateLocations:
                            false,

                          alternatives:
                            0,
                        }),
                    }
                  );

                const data =
                  await response
                    .json()
                    .catch(
                      () =>
                        null
                    );

                if (
                  !response.ok
                ) {
                  throw new Error(
                    data?.error ||
                      data?.message ||
                      "Não foi possível calcular a pré-visualização da rota."
                  );
                }

                const firstRoute =
                  Array.isArray(
                    data?.routes
                  )
                    ? data.routes[0]
                    : null;

                const geometry =
                  normalizeGeometry(
                    firstRoute?.geometry
                  );

                if (
                  !firstRoute ||
                  !geometry
                ) {
                  throw new Error(
                    "O motor de rotas não devolveu uma pré-visualização válida."
                  );
                }

                if (
                  controller.signal
                    .aborted ||
                  previewRequestId !==
                    previewRouteRequestIdRef.current
                ) {
                  return;
                }

                setPreviewRouteResult({
                  ...firstRoute,

                  geometry,

                  source:
                    firstRoute.source ||
                    data?.provider ||
                    "VALHALLA",

                  isPreview:
                    true,
                });
              } catch (
                error
              ) {
                if (
                  error?.name !==
                    "AbortError"
                ) {
                  console.warn(
                    "[EditableShipmentRouteMap] live route preview failed:",
                    error
                  );
                }
              } finally {
                if (
                  previewRequestId ===
                    previewRouteRequestIdRef.current &&
                  !controller.signal
                    .aborted
                ) {
                  setIsCalculatingPreviewRoute(
                    false
                  );
                }
              }
            },
            requestDelay
          );
      },
      [
        apiBaseUrl,
        authToken,
        avoidTolls,
        tollTravelDate,
        tollVehicleClass,
        truck?.axleCount,
        truck?.axle_count,
        truck?.height,
        truck?.length,
        truck?.weight,
        truck?.width,
      ]
    );

  const pointsSignature =
    useMemo(
      () =>
        orderedPoints
          .map(
            (
              point
            ) =>
              [
                point.id,
                point.sequence,
                point.lat,
                point.lng,
              ].join(
                ":"
              )
          )
          .join(
            "|"
          ),
      [
        orderedPoints,
      ]
    );

  

  useEffect(
    () => {
      const wasEditable =
        previousEditableRef.current;

      previousEditableRef.current =
        editable;

      if (
        !wasEditable ||
        editable
      ) {
        return;
      }

      previewRouteAwaitingCommitRef.current =
        false;

      previewRouteTargetCacheKeyRef.current =
        "";

      previewRouteBaseSignatureRef.current =
        pointsSignature;

      clearLiveRoutePreview();

      setRejectedRoadData(
        getEmptyFeatureCollection()
      );

      setMapError(
        ""
      );

      setIsMapErrorToastVisible(
        false
      );

      onErrorRef.current?.(
        ""
      );
    },
    [
      clearLiveRoutePreview,
      editable,
      pointsSignature,
    ]
  );

  const pointIdsSignature =
    useMemo(
      () =>
        orderedPoints
          .map(
            (
              point
            ) =>
              point.id
          )
          .join(
            "|"
          ),
      [
        orderedPoints,
      ]
    );

  useEffect(
    () => {
      if (
        !previewRouteAwaitingCommitRef.current
      ) {
        return;
      }

      /*
       * Wait until the parent has committed the dragged
       * marker or newly created VIA point into `points`.
       */
      if (
        pointsSignature ===
        previewRouteBaseSignatureRef.current
      ) {
        return;
      }

      previewRouteAwaitingCommitRef.current =
        false;

      /*
       * The normal route calculation will now run using
       * the newly committed points. Keep showing the live
       * preview until that exact route is resolved.
       */
      previewRouteTargetCacheKeyRef.current =
        routeCacheKey;
    },
    [
      pointsSignature,
      routeCacheKey,
    ]
  );

  useEffect(
    () => {
      const targetCacheKey =
        previewRouteTargetCacheKeyRef.current;

      if (
        !targetCacheKey ||
        resolvedRouteCacheKey !==
          targetCacheKey
      ) {
        return;
      }

      previewRouteTargetCacheKeyRef.current =
        "";

      previewRouteBaseSignatureRef.current =
        pointsSignature;

      clearLiveRoutePreview();
    },
    [
      clearLiveRoutePreview,
      pointsSignature,
      resolvedRouteCacheKey,
    ]
  );

  useEffect(
    () => {
      return () => {
        clearLiveRoutePreview();
      };
    },
    [
      clearLiveRoutePreview,
    ]
  );

  useEffect(
    () => {
      if (
        orderedPoints.length <
          2 ||
        !routeCacheKey
      ) {
        setRouteResult(
          null
        );

        onRouteChangeRef.current?.(
          null
        );

        return;
      }

      const cachedRoute =
        getCachedRoute(
          routeCacheKey
        );

      const immediateRoute =
        cachedRoute?.routeResult
          ?.geometry
          ? cachedRoute
              .routeResult
          : null;

      if (
        immediateRoute?.geometry
      ) {
        setRouteResult(
          immediateRoute
        );

        onRouteChangeRef.current?.(
          immediateRoute
        );

        return;
      }

      setRouteResult(
        null
      );

      onRouteChangeRef.current?.(
        null
      );
    },
    [
      orderedPoints,
      pointsSignature,
      routeCacheKey,
    ]
  );

  useEffect(
    () => {
      onPointMovedRef.current =
        onPointMoved;
    },
    [
      onPointMoved,
    ]
  );

  useEffect(
    () => {
      onPointPickedRef.current =
        onPointPicked;
    },
    [
      onPointPicked,
    ]
  );

  useEffect(
    () => {
      onRouteViaPointCreatedRef.current =
        onRouteViaPointCreated;
    },
    [
      onRouteViaPointCreated,
    ]
  );

  useEffect(
    () => {
      onRouteViaPointRemovedRef.current =
        onRouteViaPointRemoved;
    },
    [
      onRouteViaPointRemoved,
    ]
  );

  useEffect(
    () => {
      onRouteChangeRef.current =
        onRouteChange;
    },
    [
      onRouteChange,
    ]
  );

  useEffect(
    () => {
      onRouteValidationChangeRef.current =
        onRouteValidationChange;
    },
    [
      onRouteValidationChange,
    ]
  );

  useEffect(
    () => {
      onErrorRef.current =
        onError;
    },
    [
      onError,
    ]
  );

  useEffect(
    () => {
      if (
        !mapError
      ) {
        setIsMapErrorToastVisible(
          false
        );

        return undefined;
      }

      setIsMapErrorToastVisible(
        true
      );

      const timer =
        window.setTimeout(
          () => {
            setIsMapErrorToastVisible(
              false
            );
          },
          6500
        );

      return () => {
        window.clearTimeout(
          timer
        );
      };
    },
    [
      mapError,
    ]
  );


  useEffect(
    () => {
      const handleOutsideClick =
        (
          event
        ) => {
          if (
            searchContainerRef.current
              ?.contains(
                event.target
              )
          ) {
            return;
          }

          setIsMapSearchOpen(
            false
          );
        };

      document.addEventListener(
        "mousedown",
        handleOutsideClick
      );

      return () => {
        document.removeEventListener(
          "mousedown",
          handleOutsideClick
        );
      };
    },
    []
  );

  useEffect(
    () => {
      if (
        !showSearch
      ) {
        return undefined;
      }

      const query =
        mapSearchText.trim();

      searchRequestControllerRef
        .current
        ?.abort();

      searchRequestControllerRef.current =
        null;

      if (
        !query
      ) {
        setMapSearchResults(
          []
        );

        setMapSearchError(
          ""
        );

        setIsMapSearching(
          false
        );

        setIsMapSearchOpen(
          false
        );

        return undefined;
      }

      const coordinates =
        parseMapSearchCoordinates(
          query
        );

      if (
        coordinates
      ) {
        setMapSearchResults([
          createCoordinateSearchResult(
            coordinates
          ),
        ]);

        setMapSearchError(
          ""
        );

        setIsMapSearching(
          false
        );

        setIsMapSearchOpen(
          true
        );

        return undefined;
      }

      if (
        query.length <
        3
      ) {
        setMapSearchResults(
          []
        );

        setMapSearchError(
          "Escreve pelo menos 3 caracteres."
        );

        setIsMapSearching(
          false
        );

        setIsMapSearchOpen(
          true
        );

        return undefined;
      }

      const controller =
        new AbortController();

      searchRequestControllerRef.current =
        controller;

      const timer =
        window.setTimeout(
          async () => {
            setIsMapSearching(
              true
            );

            setMapSearchError(
              ""
            );

            setIsMapSearchOpen(
              true
            );

            try {
              const endpoint =
                `${String(
                  apiBaseUrl
                ).replace(
                  /\/$/,
                  ""
                )}/maps/geocode`;

              const requestUrl =
                new URL(
                  endpoint,
                  window.location.origin
                );

              requestUrl.searchParams.set(
                "q",
                query
              );

              requestUrl.searchParams.set(
                "language",
                "pt-PT"
              );

              requestUrl.searchParams.set(
                "limit",
                "6"
              );

              const headers = {
                Accept:
                  "application/json",
              };

              if (
                authToken
              ) {
                headers.Authorization =
                  `Bearer ${authToken}`;
              }

              const response =
                await fetch(
                  requestUrl.toString(),
                  {
                    method:
                      "GET",

                    cache:
                      "no-store",

                    signal:
                      controller.signal,

                    headers,
                  }
                );

              const data =
                await response
                  .json()
                  .catch(
                    () =>
                      null
                  );

              if (
                !response.ok
              ) {
                throw new Error(
                  data?.error ||
                    data?.message ||
                    "Não foi possível pesquisar este local."
                );
              }

              const results =
                (
                  Array.isArray(
                    data?.items
                  )
                    ? data.items
                    : []
                ).filter(
                  (
                    item
                  ) =>
                    Boolean(
                      getMapSearchResultCoordinates(
                        item
                      )
                    )
                );

              setMapSearchResults(
                results
              );

              setMapSearchError(
                results.length >
                  0
                  ? ""
                  : "Não encontrámos nenhum local com essa pesquisa."
              );
            } catch (
              error
            ) {
              if (
                error?.name ===
                "AbortError"
              ) {
                return;
              }

              console.error(
                "[EditableShipmentRouteMap] map search failed:",
                error
              );

              setMapSearchResults(
                []
              );

              setMapSearchError(
                error?.message ||
                  "Não foi possível pesquisar este local."
              );
            } finally {
              if (
                !controller.signal
                  .aborted
              ) {
                setIsMapSearching(
                  false
                );
              }
            }
          },
          450
        );

      return () => {
        window.clearTimeout(
          timer
        );

        controller.abort();
      };
    },
    [
      apiBaseUrl,
      authToken,
      mapSearchText,
      showSearch,
    ]
  );

  useEffect(
    () => {
      return () => {
        searchRequestControllerRef
          .current
          ?.abort();

        try {
          searchMarkerRef.current
            ?.remove();
        } catch {}

        searchMarkerRef.current =
          null;
      };
    },
    []
  );

  const handleSelectMapSearchResult =
    (
      item
    ) => {
      const coordinates =
        getMapSearchResultCoordinates(
          item
        );

      const map =
        mapRef.current;

      if (
        !coordinates ||
        !map
      ) {
        setMapSearchError(
          "O local selecionado não possui coordenadas válidas."
        );

        setIsMapSearchOpen(
          true
        );

        return;
      }

      const normalizedLocation = {
        ...item,

        lat:
          coordinates.lat,

        lng:
          coordinates.lng,

        latitude:
          coordinates.lat,

        longitude:
          coordinates.lng,

        coordinates:
          `${coordinates.lat.toFixed(
            7
          )}, ${coordinates.lng.toFixed(
            7
          )}`,
      };

      try {
        searchMarkerRef.current
          ?.remove();
      } catch {}

      searchMarkerRef.current =
        new maplibregl.Marker({
          color:
            "#0b74ff",

          scale:
            0.82,
        })
          .setLngLat([
            coordinates.lng,
            coordinates.lat,
          ])
          .addTo(
            map
          );

      const currentZoom =
        Number(
          map.getZoom()
        );

      map.easeTo({
        center: [
          coordinates.lng,
          coordinates.lat,
        ],

        zoom:
          Math.max(
            Number.isFinite(
              currentZoom
            )
              ? currentZoom
              : 0,
            14.5
          ),

        duration:
          650,
      });

      setMapSearchResults(
        []
      );

      setMapSearchError(
        ""
      );

      setIsMapSearchOpen(
        false
      );

      if (
        editable &&
        pickingPointId &&
        typeof onSearchResultSelected ===
          "function"
      ) {
        onSearchResultSelected(
          String(
            pickingPointId
          ),
          normalizedLocation
        );
      }
    };

  const handleClearMapSearch =
    () => {
      searchRequestControllerRef
        .current
        ?.abort();

      searchRequestControllerRef.current =
        null;

      setMapSearchText(
        ""
      );

      setMapSearchResults(
        []
      );

      setMapSearchError(
        ""
      );

      setIsMapSearching(
        false
      );

      setIsMapSearchOpen(
        false
      );

      try {
        searchMarkerRef.current
          ?.remove();
      } catch {}

      searchMarkerRef.current =
        null;
    };

  const handleMapSearchSubmit =
    (
      event
    ) => {
      event.preventDefault();

      const firstResult =
        mapSearchResults[0];

      if (
        firstResult
      ) {
        handleSelectMapSearchResult(
          firstResult
        );
      }
    };

  useEffect(
    () => {
      if (
        showSearch
      ) {
        return undefined;
      }

      searchRequestControllerRef.current
        ?.abort();

      searchRequestControllerRef.current =
        null;

      setMapSearchText(
        ""
      );

      setMapSearchResults(
        []
      );

      setMapSearchError(
        ""
      );

      setIsMapSearching(
        false
      );

      setIsMapSearchOpen(
        false
      );

      try {
        searchMarkerRef.current
          ?.remove();
      } catch {}

      searchMarkerRef.current =
        null;

      return undefined;
    },
    [
      showSearch,
    ]
  );


  useEffect(
    () => {
      if (
        !mapContainerRef.current
      ) {
        return undefined;
      }

      ensurePmtilesProtocol();

      const map =
        new maplibregl.Map({
          container:
            mapContainerRef.current,

          style:
            MAP_STYLE,

          center:
            DEFAULT_CENTER,

          zoom:
            DEFAULT_ZOOM,

          attributionControl:
            true,
        });

      mapRef.current =
        map;

      /*
       * TEMP DEBUG:
       * expose the active MapLibre instance so we can
       * inspect the exact PMTiles source being rendered.
       */
      window.__loadevoMap =
        map;

      map.addControl(
        new maplibregl.NavigationControl(),
        "top-right"
      );

      map.addControl(
        new MapZoomLevelControl(),
        "top-left"
      );

      map.addControl(
        new maplibregl.ScaleControl({
          maxWidth:
            140,

          unit:
            "metric",
        }),
        "bottom-left"
      );

      const handleLoad =
        () => {
          setMapReady(
            true
          );

          setMapError(
            ""
          );

          try {
            ensurePortugalRoadNetworkSource(
              map
            );

            ensureRoadShieldImages(
              map
            );

            ensurePortugalRoadShieldLayers(
              map
            );
          } catch (
            error
          ) {
            console.warn(
              "[EditableShipmentRouteMap] Initial Portuguese road-network setup failed:",
              error
            );
          }

          window.requestAnimationFrame(
            () => {
              try {
                map.resize();
              } catch {}
            }
          );
        };

      const scheduleRoadNetworkRetry =
        () => {
          if (
            roadNetworkRetryTimerRef.current !==
              null
          ) {
            return;
          }

          const retryAttempt =
            roadNetworkRetryAttemptRef.current;

          if (
            retryAttempt >=
            ROAD_NETWORK_RETRY_DELAYS_MS.length
          ) {
            console.warn(
              "[EditableShipmentRouteMap] Portuguese road-network retry limit reached."
            );

            return;
          }

          const retryDelay =
            ROAD_NETWORK_RETRY_DELAYS_MS[
              retryAttempt
            ];

          roadNetworkRetryAttemptRef.current =
            retryAttempt +
            1;

          roadNetworkRetryTimerRef.current =
            window.setTimeout(
              () => {
                roadNetworkRetryTimerRef.current =
                  null;

                if (
                  mapRef.current !==
                    map
                ) {
                  return;
                }

                const reloadKey =
                  `${Date.now()}-${retryAttempt + 1}`;

                console.warn(
                  `[EditableShipmentRouteMap] Reloading Portuguese road network after transient PMTiles failure. Attempt ${retryAttempt + 1}.`
                );

                removePortugalRoadNetworkSource(
                  map
                );

                setRoadNetworkReloadKey(
                  reloadKey
                );
              },
              retryDelay
            );
        };

      const handleSourceData =
        (
          event
        ) => {
          if (
            event?.sourceId !==
              ROAD_SHIELDS_SOURCE_ID ||
            event?.isSourceLoaded !==
              true
          ) {
            return;
          }

          roadNetworkRetryAttemptRef.current =
            0;

          try {
            ensureRoadShieldImages(
              map
            );

            ensurePortugalRoadShieldLayers(
              map
            );

            map.triggerRepaint();
          } catch (
            error
          ) {
            console.warn(
              "[EditableShipmentRouteMap] Could not finalize Portuguese road-number rendering:",
              error
            );
          }
        };

      const handleStyleImageMissing =
        (
          event
        ) => {
          if (
            !ROAD_SHIELD_IMAGE_IDS.has(
              event?.id
            )
          ) {
            return;
          }

          try {
            ensureRoadShieldImages(
              map
            );

            map.triggerRepaint();
          } catch (
            error
          ) {
            console.warn(
              "[EditableShipmentRouteMap] Could not restore a Portuguese road-shield image:",
              error
            );
          }
        };

      const handleError =
        (
          event
        ) => {
          const message =
            String(
              event?.error?.message ||
                event?.error ||
                ""
            ).trim();

          const normalizedMessage =
            message.toLowerCase();

          const isStyleExpressionWarning =
            normalizedMessage.includes(
              "expected value to be of type number"
            );

          const isOpenFreeMapTileFailure =
            normalizedMessage.includes(
              "tiles.openfreemap.org"
            ) &&
            (
              normalizedMessage.includes(
                "failed to fetch"
              ) ||
              normalizedMessage.includes(
                "ajaxerror"
              )
            );

          const isPmtilesByteRangeFailure =
            normalizedMessage.includes(
              "server returned no content-length header"
            ) ||
            normalizedMessage.includes(
              "content-length exceeding request"
            ) ||
            normalizedMessage.includes(
              "supports http byte serving"
            );

          /*
           * A single missing vector tile or a transient
           * PMTiles range response must not be presented
           * as a forbidden-road validation error.
           *
           * MapLibre can keep the rest of the map usable,
           * while the backend route validation remains
           * independent from these visual tile requests.
           */
          if (
            isPmtilesByteRangeFailure
          ) {
            console.warn(
              "[EditableShipmentRouteMap] transient PMTiles road-network error:",
              event?.error ||
                event
            );

            scheduleRoadNetworkRetry();

            return;
          }

          if (
            isStyleExpressionWarning ||
            isOpenFreeMapTileFailure
          ) {
            console.warn(
              "[EditableShipmentRouteMap] non-fatal map resource error:",
              event?.error ||
                event
            );

            return;
          }

          console.error(
            "[EditableShipmentRouteMap] map error:",
            event?.error ||
              event
          );

          const visibleMessage =
            message ||
            "Não foi possível carregar o mapa.";

          setMapErrorKind(
            "MAP"
          );

          setMapError(
            visibleMessage
          );

          onErrorRef.current?.(
            visibleMessage
          );
        };

      map.on(
        "load",
        handleLoad
      );

      map.on(
        "error",
        handleError
      );

      map.on(
        "sourcedata",
        handleSourceData
      );

      map.on(
        "styleimagemissing",
        handleStyleImageMissing
      );

      const resizeObserver =
        typeof ResizeObserver !==
        "undefined"
          ? new ResizeObserver(
              () => {
                try {
                  map.resize();
                } catch {}
              }
            )
          : null;

      resizeObserver?.observe(
        mapContainerRef.current
      );

      return () => {
        resizeObserver?.disconnect();

        markersRef.current.forEach(
          (
            marker
          ) => {
            try {
              marker.remove();
            } catch {}
          }
        );

        markersRef.current =
          [];

        if (
          roadNetworkRetryTimerRef.current !==
            null
        ) {
          window.clearTimeout(
            roadNetworkRetryTimerRef.current
          );

          roadNetworkRetryTimerRef.current =
            null;
        }

        try {
          map.off(
            "load",
            handleLoad
          );

          map.off(
            "error",
            handleError
          );

          map.off(
            "sourcedata",
            handleSourceData
          );

          map.off(
            "styleimagemissing",
            handleStyleImageMissing
          );

          map.remove();
        } catch {}

        if (
          mapRef.current ===
          map
        ) {
          mapRef.current =
            null;
        }
      };
    },
    []
  );

  useEffect(
    () => {
      const map =
        mapRef.current;

      if (
        !map ||
        !mapReady
      ) {
        return undefined;
      }

      if (
        !editable ||
        !pickingPointId
      ) {
        setIsPickingLocation(
          false
        );

        try {
          map.getCanvas().style.cursor =
            "";
        } catch {}

        return undefined;
      }

      let cancelled =
        false;

      let isProcessingClick =
        false;

      let activeController =
        null;

      const handleMapClick =
        async (
          event
        ) => {
          if (
            !event?.lngLat ||
            isProcessingClick
          ) {
            return;
          }

          const lat =
            Number(
              event.lngLat.lat
            );

          const lng =
            Number(
              event.lngLat.lng
            );

          if (
            !Number.isFinite(
              lat
            ) ||
            !Number.isFinite(
              lng
            )
          ) {
            return;
          }

          isProcessingClick =
            true;

          setIsPickingLocation(
            true
          );

          setMapError(
            ""
          );

          try {
            map.getCanvas().style.cursor =
              "wait";
          } catch {}

          activeController =
            new AbortController();

          let location =
            null;

          try {
            const endpoint =
              `${String(
                apiBaseUrl
              ).replace(
                /\/$/,
                ""
              )}/maps/reverse-geocode`;

            const requestUrl =
              new URL(
                endpoint,
                window.location.origin
              );

            requestUrl.searchParams.set(
              "lat",
              String(
                lat
              )
            );

            requestUrl.searchParams.set(
              "lng",
              String(
                lng
              )
            );

            requestUrl.searchParams.set(
              "language",
              "pt-PT"
            );

            const headers = {
              Accept:
                "application/json",
            };

            if (
              authToken
            ) {
              headers.Authorization =
                `Bearer ${authToken}`;
            }

            const response =
              await fetch(
                requestUrl.toString(),
                {
                  method:
                    "GET",

                  cache:
                    "no-store",

                  signal:
                    activeController.signal,

                  headers,
                }
              );

            const data =
              await response
                .json()
                .catch(
                  () =>
                    null
                );

            if (
              response.ok
            ) {
              location =
                data?.item ||
                null;
            }
          } catch (
            error
          ) {
            if (
              error?.name !==
              "AbortError"
            ) {
              console.warn(
                "[EditableShipmentRouteMap] map point reverse geocode failed:",
                error
              );
            }
          }

          if (
            !cancelled
          ) {
            onPointPickedRef.current?.({
              pointId:
                String(
                  pickingPointId
                ),

              lat,

              lng,

              location,
            });
          }

          isProcessingClick =
            false;

          if (
            !cancelled
          ) {
            setIsPickingLocation(
              false
            );

            try {
              map.getCanvas().style.cursor =
                "crosshair";
            } catch {}
          }
        };

      try {
        map.getCanvas().style.cursor =
          "crosshair";
      } catch {}

      map.on(
        "click",
        handleMapClick
      );

      return () => {
        cancelled =
          true;

        isProcessingClick =
          false;

        activeController?.abort();

        try {
          map.off(
            "click",
            handleMapClick
          );

          map.getCanvas().style.cursor =
            "";
        } catch {}
      };
    },
    [
      apiBaseUrl,
      authToken,
      editable,
      mapReady,
      pickingPointId,
    ]
  );

  useEffect(
    () => {
      const map =
        mapRef.current;

      if (
        !map ||
        !mapReady
      ) {
        return undefined;
      }

      markersRef.current.forEach(
        (
          marker
        ) => {
          try {
            marker.remove();
          } catch {}
        }
      );

      markersRef.current =
        [];

      const abortControllers =
        [];

      orderedPoints.forEach(
        (
          point,
          index
        ) => {
          const element =
            createMarkerElement({
              point,
              index,
              editable,
            });

          const marker =
            new maplibregl.Marker({
              element,

              draggable:
                editable,

              anchor:
                "center",
            })
              .setLngLat([
                point.lng,
                point.lat,
              ])
              .addTo(
                map
              );

          if (
            editable
          ) {
            const buildMarkerPreviewPoints =
              () => {
                const {
                  lng,
                  lat,
                } =
                  marker.getLngLat();

                return orderedPoints.map(
                  (
                    routePoint
                  ) => {
                    if (
                      String(
                        routePoint.id
                      ) !==
                      String(
                        point.id
                      )
                    ) {
                      return routePoint;
                    }

                    return {
                      ...routePoint,

                      lat:
                        Number(
                          lat
                        ),

                      lng:
                        Number(
                          lng
                        ),
                    };
                  }
                );
              };

            marker.on(
              "dragstart",
              () => {
                setRejectedRoadData(
                  getEmptyFeatureCollection()
                );

                setMapError(
                  ""
                );

                onErrorRef.current?.(
                  ""
                );

                previewRouteBaseSignatureRef.current =
                  pointsSignature;

                previewRouteAwaitingCommitRef.current =
                  false;

                previewRouteTargetCacheKeyRef.current =
                  "";

                queueLiveRoutePreview(
                  buildMarkerPreviewPoints()
                );
              }
            );

            marker.on(
              "drag",
              () => {
                queueLiveRoutePreview(
                  buildMarkerPreviewPoints()
                );
              }
            );

            marker.on(
              "dragend",
              async () => {
                const {
                  lng,
                  lat,
                } =
                  marker.getLngLat();

                queueLiveRoutePreview(
                  buildMarkerPreviewPoints()
                );

                previewRouteAwaitingCommitRef.current =
                  true;

                let location =
                  null;

                const controller =
                  new AbortController();

                abortControllers.push(
                  controller
                );

                try {
                  const endpoint =
                    `${String(
                      apiBaseUrl
                    ).replace(
                      /\/$/,
                      ""
                    )}/maps/reverse-geocode`;

                  const requestUrl =
                    new URL(
                      endpoint,
                      window.location
                        .origin
                    );

                  requestUrl.searchParams.set(
                    "lat",
                    String(
                      lat
                    )
                  );

                  requestUrl.searchParams.set(
                    "lng",
                    String(
                      lng
                    )
                  );

                  const headers = {
                    Accept:
                      "application/json",
                  };

                  if (
                    authToken
                  ) {
                    headers.Authorization =
                      `Bearer ${authToken}`;
                  }

                  const response =
                    await fetch(
                      requestUrl.toString(),
                      {
                        method:
                          "GET",

                        signal:
                          controller.signal,

                        headers,
                      }
                    );

                  const data =
                    await response
                      .json()
                      .catch(
                        () =>
                          null
                      );

                  if (
                    response.ok
                  ) {
                    location =
                      data?.item ||
                      null;
                  }
                } catch (
                  error
                ) {
                  if (
                    error?.name !==
                    "AbortError"
                  ) {
                    console.warn(
                      "[EditableShipmentRouteMap] reverse geocode failed:",
                      error
                    );
                  }
                }

                onPointMovedRef.current?.({
                  pointId:
                    point.id,

                  point,

                  lat:
                    Number(
                      lat
                    ),

                  lng:
                    Number(
                      lng
                    ),

                  location,
                });
              }
            );
          }


                    if (
            editable &&
            String(
              point?.type ||
                ""
            ).toUpperCase() ===
              "VIA"
          ) {
            element.title =
              "Ponto de passagem · duplo clique para remover";

            element.addEventListener(
              "dblclick",
              (
                event
              ) => {
                event.preventDefault();

                event.stopPropagation();

                onRouteViaPointRemovedRef.current?.(
                  point.id
                );
              }
            );
          }

          markersRef.current.push(
            marker
          );
        }
      );

      if (
        orderedPoints.length >
          0 &&
        lastFitPointIdsRef.current !==
          pointIdsSignature
      ) {
        lastFitPointIdsRef.current =
          pointIdsSignature;

        if (
          orderedPoints.length ===
          1
        ) {
          map.flyTo({
            center: [
              orderedPoints[0]
                .lng,

              orderedPoints[0]
                .lat,
            ],

            zoom:
              12,
          });
        } else {
          const bounds =
            new maplibregl.LngLatBounds();

          orderedPoints.forEach(
            (
              point
            ) => {
              bounds.extend([
                point.lng,
                point.lat,
              ]);
            }
          );

          map.fitBounds(
            bounds,
            {
              padding: {
                top:
                  72,

                right:
                  72,

                bottom:
                  72,

                left:
                  72,
              },

              maxZoom:
                13,

              duration:
                450,
            }
          );
        }
      }

      return () => {
        abortControllers.forEach(
          (
            controller
          ) =>
            controller.abort()
        );
      };
    },
    [
      apiBaseUrl,
      authToken,
      editable,
      mapReady,
      orderedPoints,
      pointIdsSignature,
      pointsSignature,
      queueLiveRoutePreview,
    ]
  );

  useEffect(
    () => {
      const map =
        mapRef.current;

      if (
        !map ||
        !mapReady ||
        !editable ||
        pickingPointId ||
        orderedPoints.length <
          2
      ) {
        return undefined;
      }

      const isMarkerEvent =
        (
          event
        ) => {
          const eventTarget =
            event
              ?.originalEvent
              ?.target;

          if (
            !eventTarget ||
            typeof eventTarget.closest !==
              "function"
          ) {
            return false;
          }

          return Boolean(
            eventTarget.closest(
              ".editable-shipment-route-marker"
            ) ||
            eventTarget.closest(
              ".editable-shipment-route-via-preview"
            )
          );
        };

      const getRouteFeature =
        (
          event
        ) => {
          if (
            !event?.point ||
            isMarkerEvent(
              event
            )
          ) {
            return null;
          }

          const layers = [
            ROUTE_LINE_LAYER_ID,
            ROUTE_CASING_LAYER_ID,
          ].filter(
            (
              layerId
            ) =>
              Boolean(
                map.getLayer(
                  layerId
                )
              )
          );

          if (
            layers.length ===
            0
          ) {
            return null;
          }

          const tolerance =
            10;

          try {
            const features =
              map.queryRenderedFeatures(
                [
                  [
                    event.point.x -
                      tolerance,
                    event.point.y -
                      tolerance,
                  ],
                  [
                    event.point.x +
                      tolerance,
                    event.point.y +
                      tolerance,
                  ],
                ],
                {
                  layers,
                }
              );

            return (
              features[0] ||
              null
            );
          } catch {
            return null;
          }
        };

      const removePreviewMarker =
        () => {
          try {
            routeLinePreviewMarkerRef
              .current
              ?.remove();
          } catch {}

          routeLinePreviewMarkerRef.current =
            null;
        };

      const restoreMapInteraction =
        () => {
          try {
            if (
              !map.dragPan.isEnabled()
            ) {
              map.dragPan.enable();
            }

            map.getCanvas().style.cursor =
              "";
          } catch {}
        };

      const buildLinePreviewPoints =
        (
          dragState
        ) => {
          if (
            !dragState ||
            dragState.segmentIndex <
              0
          ) {
            return orderedPoints;
          }

          const temporaryViaPoint = {
            id:
              "__live-route-preview-via__",

            type:
              "VIA",

            lat:
              Number(
                dragState.lat
              ),

            lng:
              Number(
                dragState.lng
              ),
          };

          return [
            ...orderedPoints.slice(
              0,
              dragState.segmentIndex +
                1
            ),

            temporaryViaPoint,

            ...orderedPoints.slice(
              dragState.segmentIndex +
                1
            ),
          ].map(
            (
              point,
              index
            ) => ({
              ...point,

              sequence:
                index,
            })
          );
        };

      const handleMouseDown =
        (
          event
        ) => {
          if (
            event
              ?.originalEvent
              ?.button !==
              undefined &&
            event
              .originalEvent
              .button !==
              0
          ) {
            return;
          }

          /*
           * Existing pickup, delivery and VIA markers
           * have their own MapLibre drag behaviour.
           *
           * Do not start line dragging when the pointer
           * originated from one of those markers, even
           * though the blue route exists underneath it.
           */
          if (
            isMarkerEvent(
              event
            )
          ) {
            return;
          }

          if (
            !event?.lngLat ||
            !getRouteFeature(
              event
            )
          ) {
            return;
          }

          const segmentIndex =
            getNearestOrderedPointSegmentIndex(
              map,
              orderedPoints,
              event.lngLat
            );

          if (
            segmentIndex <
              0 ||
            segmentIndex >=
              orderedPoints.length -
                1
          ) {
            return;
          }

          event.preventDefault?.();

          setRejectedRoadData(
            getEmptyFeatureCollection()
          );

          setMapError(
            ""
          );

          onErrorRef.current?.(
            ""
          );

          previewRouteBaseSignatureRef.current =
            pointsSignature;

          previewRouteAwaitingCommitRef.current =
            false;

          previewRouteTargetCacheKeyRef.current =
            "";

          routeLineDragStateRef.current = {
            segmentIndex,

            afterPointId:
              orderedPoints[
                segmentIndex
              ].id,

            beforePointId:
              orderedPoints[
                segmentIndex +
                  1
              ].id,

            lat:
              Number(
                event.lngLat.lat
              ),

            lng:
              Number(
                event.lngLat.lng
              ),

            startX:
              Number(
                event.point?.x
              ),

            startY:
              Number(
                event.point?.y
              ),

            moved:
              false,
          };

          removePreviewMarker();

          const previewElement =
            createRouteViaPreviewElement();

          routeLinePreviewMarkerRef.current =
            new maplibregl.Marker({
              element:
                previewElement,

              anchor:
                "center",
            })
              .setLngLat([
                event.lngLat.lng,
                event.lngLat.lat,
              ])
              .addTo(
                map
              );

          setIsDraggingRoute(
            true
          );

          try {
            map.dragPan.disable();

            map.getCanvas().style.cursor =
              "grabbing";
          } catch {}
        };

      const handleMouseMove =
        (
          event
        ) => {
          const dragState =
            routeLineDragStateRef.current;

          if (
            !dragState
          ) {
            if (
              isMarkerEvent(
                event
              )
            ) {
              map.getCanvas().style.cursor =
                "";

              return;
            }

            map.getCanvas().style.cursor =
              getRouteFeature(
                event
              )
                ? "grab"
                : "";

            return;
          }

          if (
            !event?.lngLat
          ) {
            return;
          }

          dragState.lat =
            Number(
              event.lngLat.lat
            );

          dragState.lng =
            Number(
              event.lngLat.lng
            );

          const movementDistance =
            Math.hypot(
              Number(
                event.point?.x
              ) -
                dragState.startX,
              Number(
                event.point?.y
              ) -
                dragState.startY
            );

          if (
            movementDistance >
            3
          ) {
            dragState.moved =
              true;
          }

          if (
            dragState.moved
          ) {
            queueLiveRoutePreview(
              buildLinePreviewPoints(
                dragState
              )
            );
          }

          routeLinePreviewMarkerRef
            .current
            ?.setLngLat([
              dragState.lng,
              dragState.lat,
            ]);

          event.preventDefault?.();
        };

      const finishRouteDragging =
        (
          event = null
        ) => {
          const dragState =
            routeLineDragStateRef.current;

          if (
            !dragState
          ) {
            return;
          }

          if (
            event?.lngLat
          ) {
            dragState.lat =
              Number(
                event.lngLat.lat
              );

            dragState.lng =
              Number(
                event.lngLat.lng
              );
          }

          routeLineDragStateRef.current =
            null;

          removePreviewMarker();

          setIsDraggingRoute(
            false
          );

          restoreMapInteraction();

          if (
            !dragState.moved
          ) {
            clearLiveRoutePreview();

            return;
          }

          queueLiveRoutePreview(
            buildLinePreviewPoints(
              dragState
            )
          );

          previewRouteAwaitingCommitRef.current =
            true;

          onRouteViaPointCreatedRef.current?.({
            afterPointId:
              dragState.afterPointId,

            beforePointId:
              dragState.beforePointId,

            lat:
              dragState.lat,

            lng:
              dragState.lng,
          });
        };

      const handleMouseUp =
        (
          event
        ) => {
          finishRouteDragging(
            event
          );
        };

      const handleWindowMouseUp =
        () => {
          finishRouteDragging();
        };

      map.on(
        "mousedown",
        handleMouseDown
      );

      map.on(
        "mousemove",
        handleMouseMove
      );

      map.on(
        "mouseup",
        handleMouseUp
      );

      window.addEventListener(
        "mouseup",
        handleWindowMouseUp
      );

      return () => {
        routeLineDragStateRef.current =
          null;

        removePreviewMarker();

        setIsDraggingRoute(
          false
        );

        try {
          map.off(
            "mousedown",
            handleMouseDown
          );

          map.off(
            "mousemove",
            handleMouseMove
          );

          map.off(
            "mouseup",
            handleMouseUp
          );
        } catch {}

        window.removeEventListener(
          "mouseup",
          handleWindowMouseUp
        );

        restoreMapInteraction();
      };
    },
    [
      clearLiveRoutePreview,
      editable,
      mapReady,
      orderedPoints,
      pickingPointId,
      pointsSignature,
      queueLiveRoutePreview,
      routeResult,
    ]
  );

  useEffect(
    () => {
      if (
        orderedPoints.length <
          2 ||
        !routeCacheKey
      ) {
        onRouteValidationChangeRef.current?.({
          status:
            "IDLE",

          routeValid:
            false,

          allLocationsAccessible:
            false,

          locations:
            [],
        });

        routeRequestIdRef.current +=
          1;

        activeRouteCacheKeyRef.current =
          "";

        setRouteResult(
          null
        );

        setRejectedRoadData(
          getEmptyFeatureCollection()
        );

        setResolvedRouteCacheKey(
          ""
        );

        setIsCalculatingRoute(
          false
        );

        setMapError(
          ""
        );

        onRouteChangeRef.current?.(
          null
        );

        onErrorRef.current?.(
          ""
        );

        return undefined;
      }

      const previousRouteCacheKey =
        activeRouteCacheKeyRef.current;

      const requestId =
        routeRequestIdRef.current +
        1;

      routeRequestIdRef.current =
        requestId;

      activeRouteCacheKeyRef.current =
        routeCacheKey;

      const controller =
        new AbortController();

      setRejectedRoadData(
        getEmptyFeatureCollection()
      );

      onRouteValidationChangeRef.current?.({
        status:
          "CHECKING",

        routeValid:
          false,

        allLocationsAccessible:
          false,

        locations:
          [],
      });

      const cachedRoute =
        getCachedRoute(
          routeCacheKey
        );

      if (
        cachedRoute?.routeResult
          ?.geometry
      ) {
        setRouteResult(
          cachedRoute.routeResult
        );

        setResolvedRouteCacheKey(
          routeCacheKey
        );

        onRouteChangeRef.current?.(
          cachedRoute.routeResult
        );
      } else if (
        previousRouteCacheKey &&
        previousRouteCacheKey !==
          routeCacheKey
      ) {
        setRouteResult(
          null
        );

        onRouteChangeRef.current?.(
          null
        );
      }

      const isLatestRequest =
        () =>
          !controller.signal
            .aborted &&
          requestId ===
            routeRequestIdRef
              .current &&
          routeCacheKey ===
            activeRouteCacheKeyRef
              .current;

      const timer =
        window.setTimeout(
          async () => {
            if (
              !isLatestRequest()
            ) {
              return;
            }

            setIsCalculatingRoute(
              true
            );

            setMapError(
              ""
            );

            onErrorRef.current?.(
              ""
            );

            try {
              const endpoint =
                `${String(
                  apiBaseUrl
                ).replace(
                  /\/$/,
                  ""
                )}/maps/route`;

              const truckOptions = {
                height:
                  Number(
                    truck?.height
                  ) ||
                  undefined,

                width:
                  Number(
                    truck?.width
                  ) ||
                  undefined,

                length:
                  Number(
                    truck?.length
                  ) ||
                  undefined,

                weight:
                  Number(
                    truck?.weight
                  ) ||
                  undefined,

                axleCount:
                  Number(
                    truck?.axleCount ??
                      truck?.axle_count
                  ) ||
                  undefined,
              };

              Object.keys(
                truckOptions
              ).forEach(
                (
                  key
                ) => {
                  if (
                    truckOptions[
                      key
                    ] ===
                    undefined
                  ) {
                    delete truckOptions[
                      key
                    ];
                  }
                }
              );

              const response =
                await fetch(
                  endpoint,
                  {
                    method:
                      "POST",

                    signal:
                      controller.signal,

                    headers: {
                      Accept:
                        "application/json",

                      "Content-Type":
                        "application/json",

                      Authorization:
                        `Bearer ${authToken}`,
                    },

                    body:
                      JSON.stringify({
                        locations:
                          orderedPoints.map(
                            (
                              point
                            ) => ({
                              id:
                                point.id,

                              name:
                                point?.name ||
                                point?.placeName ||
                                point?.location ||
                                null,

                              pointKind:
                                point?.pointKind ||
                                point?.type ||
                                null,

                              lat:
                                point.lat,

                              lon:
                                point.lng,
                            })
                          ),

                        costing:
                          "truck",

                        truck:
                          truckOptions,

                        avoidTolls:
                          Boolean(
                            avoidTolls
                          ),

                        includeTollEstimate:
                          true,

                        tollVehicleClass,

                        tollTravelDate,

                        validateLocations:
                          false,

                        alternatives:
                          0,
                      }),
                  }
                );

              const data =
                await response
                  .json()
                  .catch(
                    () =>
                      null
                  );

              if (
                !response.ok
              ) {
                const routeError =
                  new Error(
                    data?.error ||
                      data?.message ||
                      "Não foi possível calcular a rota."
                  );

                routeError.code =
                  data?.code ||
                  null;

                routeError.locationValidation =
                  data
                    ?.locationValidation ||
                  null;

                throw routeError;
              }

              const locationValidation =
                data
                  ?.locationValidation ||
                null;

              /*
               * Route validation is auxiliary.
               *
               * A successfully calculated Valhalla truck
               * route must not be discarded merely because
               * locationValidation is absent or unavailable.
               *
               * Only reject the route when the backend
               * explicitly confirms that one or more
               * locations are inaccessible.
               */
              if (
                locationValidation
                  ?.allAccessible ===
                  false
              ) {
                const validationError =
                  new Error(
                    "Uma das localizações não é acessível ao veículo selecionado."
                  );

                validationError.locationValidation =
                  locationValidation;

                throw validationError;
              }

              const firstRoute =
                Array.isArray(
                  data?.routes
                )
                  ? data.routes[0]
                  : null;

              const geometry =
                normalizeGeometry(
                  firstRoute?.geometry
                );

              if (
                !firstRoute ||
                !geometry
              ) {
                throw new Error(
                  "O motor de rotas não devolveu uma geometria válida."
                );
              }

              const normalizedRoute = {
                ...firstRoute,

                geometry,

                source:
                  firstRoute.source ||
                  data?.provider ||
                  "VALHALLA",
              };

              if (
                !isLatestRequest()
              ) {
                return;
              }

              setCachedRoute(
                routeCacheKey,
                normalizedRoute
              );

              setRouteResult(
                normalizedRoute
              );

              setResolvedRouteCacheKey(
                routeCacheKey
              );

              onRouteChangeRef.current?.(
                normalizedRoute
              );

              onRouteValidationChangeRef.current?.({
                status:
                  locationValidation
                    ?.status ||
                  "VALID",

                routeValid:
                  true,

                allLocationsAccessible:
                  locationValidation
                    ?.allAccessible !==
                  false,

                locations:
                  Array.isArray(
                    locationValidation
                      ?.locations
                  )
                    ? locationValidation
                        .locations
                    : [],
              });

              setRejectedRoadData(
                getEmptyFeatureCollection()
              );

              setMapError(
                ""
              );

              onErrorRef.current?.(
                ""
              );
            } catch (
              error
            ) {
              if (
                error?.name ===
                  "AbortError" ||
                !isLatestRequest()
              ) {
                return;
              }

              console.error(
                "[EditableShipmentRouteMap] route calculation failed:",
                error
              );

              const locationValidation =
                error
                  ?.locationValidation ||
                null;

              const hasRestrictedLocations =
                locationValidation
                  ?.allAccessible ===
                false;

              const validationLocations =
                Array.isArray(
                  locationValidation
                    ?.locations
                )
                  ? locationValidation
                      .locations
                  : [];

              const rejectedRoadFeatureCollection =
                hasRestrictedLocations
                  ? buildRejectedRoadFeatureCollection(
                      validationLocations
                    )
                  : getEmptyFeatureCollection();

              if (
                hasRestrictedLocations
              ) {
                previewRouteAwaitingCommitRef.current =
                  false;

                previewRouteTargetCacheKeyRef.current =
                  "";

                previewRouteBaseSignatureRef.current =
                  pointsSignature;

                clearLiveRoutePreview();

                const rejectedLocation =
                  getFirstRejectedLocation(
                    validationLocations
                  );

                const rejectedLatitude =
                  Number(
                    rejectedLocation
                      ?.correlatedLatitude ??
                    rejectedLocation
                      ?.latitude
                  );

                const rejectedLongitude =
                  Number(
                    rejectedLocation
                      ?.correlatedLongitude ??
                    rejectedLocation
                      ?.longitude
                  );

                if (
                  Number.isFinite(
                    rejectedLatitude
                  ) &&
                  Number.isFinite(
                    rejectedLongitude
                  )
                ) {
                  try {
                    const currentZoom =
                      Number(
                        mapRef.current
                          ?.getZoom()
                      );

                    mapRef.current?.easeTo({
                      center: [
                        rejectedLongitude,
                        rejectedLatitude,
                      ],

                      zoom:
                        Math.max(
                          Number.isFinite(
                            currentZoom
                          )
                            ? currentZoom
                            : 0,
                          13
                        ),

                      duration:
                        450,
                    });
                  } catch (
                    focusError
                  ) {
                    console.warn(
                      "[EditableShipmentRouteMap] Could not focus rejected road:",
                      focusError
                    );
                  }
                }
              }

              setRejectedRoadData(
                rejectedRoadFeatureCollection
              );

              onRouteValidationChangeRef.current?.({
                status:
                  hasRestrictedLocations
                    ? "INVALID"
                    : "ERROR",

                routeValid:
                  false,

                allLocationsAccessible:
                  false,

                locations:
                  validationLocations,
              });

              const hasCachedRoute =
                Boolean(
                  cachedRoute
                    ?.routeResult
                    ?.geometry
                );

              if (
                hasRestrictedLocations ||
                !hasCachedRoute
              ) {
                setRouteResult(
                  null
                );

                setResolvedRouteCacheKey(
                  ""
                );

                onRouteChangeRef.current?.(
                  null
                );
              }

              const message =
                hasRestrictedLocations
                  ? error?.message ||
                    "Uma das localizações não é acessível ao veículo selecionado."
                  : hasCachedRoute
                    ? "A mostrar a última rota guardada. Não foi possível confirmar uma rota atualizada."
                    : error?.message ||
                      "Não foi possível calcular a rota.";

              setMapErrorKind(
                hasRestrictedLocations
                  ? "VEHICLE_RESTRICTION"
                  : "ROUTE"
              );

              setMapError(
                message
              );

              onErrorRef.current?.(
                message
              );
            } finally {
              if (
                isLatestRequest()
              ) {
                setIsCalculatingRoute(
                  false
                );
              }
            }
          },
          280
        );

      return () => {
        window.clearTimeout(
          timer
        );

        controller.abort();
      };
    },
    [
      apiBaseUrl,
      authToken,
      avoidTolls,
      clearLiveRoutePreview,
      pointsSignature,
      routeCacheKey,
      tollTravelDate,
      tollVehicleClass,
      truck?.axleCount,
      truck?.axle_count,
      truck?.height,
      truck?.length,
      truck?.weight,
      truck?.width,
    ]
  );

  const visibleRouteResult =
    previewRouteResult
      ?.geometry
      ? previewRouteResult
      : routeResult;

  useEffect(
    () => {
      const map =
        mapRef.current;

      if (
        !map ||
        !mapReady
      ) {
        return undefined;
      }

      let frameId =
        null;

      const drawRoute =
        () => {
          if (
            !mapRef.current ||
            mapRef.current !==
              map ||
            !map.isStyleLoaded()
          ) {
            return;
          }

          /*
           * Draw the road hierarchy before the
           * blue calculated route.
           *
           * Portugal keeps the custom PMTiles
           * network, while European motorways use
           * exactly the same casing, thickness,
           * colour and zoom thresholds.
           */
          ensurePortugalRoadLineLayers(
            map,
            {
              reloadKey:
                roadNetworkReloadKey,
            }
          );

          ensureEuropeMotorwayLineLayers(
            map
          );

          ensureVehicleRestrictionLayers(
            map,
            {
              truck,

              visible:
                editable &&
                showVehicleRestrictions,
            }
          );

          const routeData =
            visibleRouteResult
              ?.geometry ||
            getEmptyFeatureCollection();

          let routeSource =
            map.getSource(
              ROUTE_SOURCE_ID
            );

          if (
            !routeSource
          ) {
            try {
              map.addSource(
                ROUTE_SOURCE_ID,
                {
                  type:
                    "geojson",

                  data:
                    routeData,
                }
              );
            } catch (
              error
            ) {
              console.warn(
                "[EditableShipmentRouteMap] Could not add route source:",
                error
              );

              return;
            }

            routeSource =
              map.getSource(
                ROUTE_SOURCE_ID
              );
          }

          if (
            routeSource &&
            typeof routeSource.setData ===
              "function"
          ) {
            routeSource.setData(
              routeData
            );
          }

          if (
            !map.getLayer(
              ROUTE_CASING_LAYER_ID
            )
          ) {
            try {
              map.addLayer({
                id:
                  ROUTE_CASING_LAYER_ID,

                type:
                  "line",

                source:
                  ROUTE_SOURCE_ID,

                layout: {
                  visibility:
                    "visible",

                  "line-cap":
                    "round",

                  "line-join":
                    "round",
                },

                paint: {
                  "line-color":
                    "#ffffff",

                  "line-width":
                    9,

                  "line-opacity":
                    1,
                },
              });
            } catch (
              error
            ) {
              console.warn(
                "[EditableShipmentRouteMap] Could not add route casing layer:",
                error
              );
            }
          }

          if (
            !map.getLayer(
              ROUTE_LINE_LAYER_ID
            )
          ) {
            try {
              map.addLayer({
                id:
                  ROUTE_LINE_LAYER_ID,

                type:
                  "line",

                source:
                  ROUTE_SOURCE_ID,

                layout: {
                  visibility:
                    "visible",

                  "line-cap":
                    "round",

                  "line-join":
                    "round",
                },

                paint: {
                  "line-color":
                    "#1677ff",

                  "line-width":
                    5,

                  "line-opacity":
                    1,
                },
              });
            } catch (
              error
            ) {
              console.warn(
                "[EditableShipmentRouteMap] Could not add route line layer:",
                error
              );
            }
          }

          if (
            map.getLayer(
              ROUTE_CASING_LAYER_ID
            )
          ) {
            try {
              map.moveLayer(
                ROUTE_CASING_LAYER_ID
              );
            } catch {}
          }

          if (
            map.getLayer(
              ROUTE_LINE_LAYER_ID
            )
          ) {
            try {
              map.moveLayer(
                ROUTE_LINE_LAYER_ID
              );
            } catch {}
          }

          ensureRejectedRoadHighlightLayers(
            map,
            {
              data:
                rejectedRoadData,
            }
          );

          /*
           * Road shields are added after the calculated
           * route, keeping A, E, IP, IC and N references
           * readable above the blue route.
           */
          ensurePortugalRoadShieldLayers(
            map,
            {
              reloadKey:
                roadNetworkReloadKey,
            }
          );
        };

      const scheduleRouteDraw =
        () => {
          if (
            frameId !==
            null
          ) {
            window.cancelAnimationFrame(
              frameId
            );
          }

          frameId =
            window.requestAnimationFrame(
              () => {
                frameId =
                  null;

                drawRoute();
              }
            );
        };

      scheduleRouteDraw();

      map.on(
        "load",
        scheduleRouteDraw
      );

      map.on(
        "styledata",
        scheduleRouteDraw
      );

      map.on(
        "idle",
        scheduleRouteDraw
      );

      return () => {
        if (
          frameId !==
          null
        ) {
          window.cancelAnimationFrame(
            frameId
          );
        }

        try {
          map.off(
            "load",
            scheduleRouteDraw
          );

          map.off(
            "styledata",
            scheduleRouteDraw
          );

          map.off(
            "idle",
            scheduleRouteDraw
          );
        } catch {}
      };
    },
    [
      editable,
      mapReady,
      rejectedRoadData,
      roadNetworkReloadKey,
      showVehicleRestrictions,
      truck?.height,
      truck?.length,
      truck?.weight,
      truck?.width,
      visibleRouteResult,
    ]
  );

  return (
    <div
      className={[
        "editable-shipment-route-map",

        showSearch
          ? "has-map-search"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <div
        ref={
          mapContainerRef
        }
        className="editable-shipment-route-map__canvas"
      />

      <div
        ref={
          searchContainerRef
        }
        className={[
          "editable-shipment-route-map__search",

          editable
            ? "is-route-editing"
            : "",
        ]
          .filter(
            Boolean
          )
          .join(
            " "
          )}
        onMouseDown={(event) =>
          event.stopPropagation()
        }
        onClick={(event) =>
          event.stopPropagation()
        }
        onDoubleClick={(event) =>
          event.stopPropagation()
        }
      >
        <form
          className="editable-shipment-route-map__search-form"
          onSubmit={
            handleMapSearchSubmit
          }
        >
          <Search
            size={
              17
            }
            aria-hidden="true"
          />

          <input
            type="search"
            value={
              mapSearchText
            }
            placeholder="Pesquisar endereço ou coordenadas"
            aria-label="Pesquisar endereço ou coordenadas no mapa"
            autoComplete="off"
            disabled={
              !mapReady
            }
            onFocus={() => {
              if (
                mapSearchResults.length >
                  0 ||
                mapSearchError
              ) {
                setIsMapSearchOpen(
                  true
                );
              }
            }}
            onChange={(event) =>
              setMapSearchText(
                event.target.value
              )
            }
          />

          {isMapSearching ? (
            <LoaderCircle
              size={
                16
              }
              className="editable-shipment-route-map__search-spinner"
              aria-label="A pesquisar"
            />
          ) : mapSearchText ? (
            <button
              type="button"
              className="editable-shipment-route-map__search-clear"
              aria-label="Limpar pesquisa"
              onClick={
                handleClearMapSearch
              }
            >
              <X
                size={
                  15
                }
              />
            </button>
          ) : null}
        </form>

        {isMapSearchOpen && (
          <div className="editable-shipment-route-map__search-results">
            {mapSearchResults.map(
              (
                item,
                index
              ) => {
                const coordinates =
                  getMapSearchResultCoordinates(
                    item
                  );

                const title =
                  getMapSearchResultTitle(
                    item
                  );

                const subtitle =
                  getMapSearchResultSubtitle(
                    item
                  );

                return (
                  <button
                    key={
                      item?.id ||
                      item?.placeId ||
                      `${coordinates?.lat}-${coordinates?.lng}-${index}`
                    }
                    type="button"
                    className="editable-shipment-route-map__search-result"
                    onClick={() =>
                      handleSelectMapSearchResult(
                        item
                      )
                    }
                  >
                    <span className="editable-shipment-route-map__search-result-icon">
                      <MapPin
                        size={
                          15
                        }
                      />
                    </span>

                    <span className="editable-shipment-route-map__search-result-copy">
                      <strong>
                        {title}
                      </strong>

                      {subtitle && (
                        <small>
                          {subtitle}
                        </small>
                      )}
                    </span>
                  </button>
                );
              }
            )}

            {!isMapSearching &&
              mapSearchError && (
                <div className="editable-shipment-route-map__search-message">
                  {mapSearchError}
                </div>
              )}

            <div className="editable-shipment-route-map__search-attribution">
              © OpenStreetMap contributors
            </div>
          </div>
        )}
      </div>

      {!mapReady &&
        !mapError && (
          <div className="editable-shipment-route-map__status">
            A carregar mapa...
          </div>
        )}

      {(isCalculatingPreviewRoute ||
        isCalculatingRoute) && (
        <div className="editable-shipment-route-map__calculating">
          {isCalculatingPreviewRoute
            ? "A calcular rota possível..."
            : "A calcular rota..."}
        </div>
      )}

      {mapError &&
        isMapErrorToastVisible && (
          <div
            className="editable-shipment-route-map__error"
            role="alert"
          >
            <span className="editable-shipment-route-map__error-icon">
              !
            </span>

            <div className="editable-shipment-route-map__error-copy">
              <strong>
                {mapErrorKind ===
                "VEHICLE_RESTRICTION"
                  ? "Estrada proibida para este veículo"
                  : mapErrorKind ===
                      "ROUTE"
                    ? "Não foi possível calcular a rota"
                    : "Problema ao carregar o mapa"}
              </strong>

              <span>
                {mapError}
              </span>
            </div>

            <button
              type="button"
              className="editable-shipment-route-map__error-close"
              aria-label="Fechar aviso"
              onClick={() =>
                setIsMapErrorToastVisible(
                  false
                )
              }
            >
              ×
            </button>
          </div>
        )}

      {editable && (
        <div
          className="editable-shipment-route-map__edit-hint"
          style={{
            pointerEvents:
              "none",
          }}
        >
          {isDraggingRoute
            ? "Larga a linha para criar um ponto de passagem"
            : pickingPointId
              ? isPickingLocation
                ? "A identificar o novo local..."
                : "Clique no mapa para colocar a paragem"
              : "Arrasta os pontos ou puxa a linha azul para alterar o trajeto"}
        </div>
      )}
    </div>
  );
}